"use client";

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { OpenIMConfig } from '@/lib/config';
import { getUserToken, getUserRole, markMessageAsRead, closeRoom, openRoom } from '@/lib/api';
import { createUniqueId, getAvatarColor, getInitials, getMessagePreviewText, getRoomDisplayName } from '@/lib/utils';
import { useLanguage } from '@/contexts/LanguageContext';
import { notificationService } from '@/services/NotificationService';
import {
    UserDto,
    UserType,
    RoomDto,
    ConversationDto,
    MessageDto,
    LayoutState,
    PositionState,
    GroupMemberDto,
    MessageType,
    MessageStatus,
    TypingStatus,
    ImageViewerData,
    VideoViewerData,
    MessageReactions,
    SearchResultItem,
    AtUsersInfoItem,
} from '@/types';

const MESSAGE_SEND_TIMEOUT_MS = 120000;

function isBrowserOffline(): boolean {
    return typeof navigator !== 'undefined' && navigator.onLine === false;
}

function isHostChatDisabled(): boolean {
    return typeof window !== 'undefined' && (window as any)._jfcChatDisabled === true;
}

// Attachment uploads can legitimately take longer than normal message sends.
// A local Promise timeout cannot cancel the SDK upload, so it can mark a file
// as failed while the SDK continues and sends it successfully.

// ============================================
// Upgrade http:// URLs to https:// in message media elements
// ============================================
function toHttps(url: string | undefined): string | undefined {
    if (!url) return url;
    return url.replace(/^http:\/\//i, 'https://');
}

function upgradeMessageUrls(msg: MessageDto): MessageDto {
    if (msg.pictureElem) {
        if (msg.pictureElem.sourcePicture?.url) msg.pictureElem.sourcePicture.url = toHttps(msg.pictureElem.sourcePicture.url)!;
        if (msg.pictureElem.bigPicture?.url) msg.pictureElem.bigPicture.url = toHttps(msg.pictureElem.bigPicture.url)!;
        if (msg.pictureElem.snapshotPicture?.url) msg.pictureElem.snapshotPicture.url = toHttps(msg.pictureElem.snapshotPicture.url)!;
    }
    if (msg.videoElem) {
        msg.videoElem.videoUrl = toHttps(msg.videoElem.videoUrl)!;
        if (msg.videoElem.snapshotUrl) msg.videoElem.snapshotUrl = toHttps(msg.videoElem.snapshotUrl)!;
    }
    if (msg.fileElem) {
        msg.fileElem.sourceUrl = toHttps(msg.fileElem.sourceUrl)!;
    }
    if (msg.soundElem) {
        msg.soundElem.sourceUrl = toHttps(msg.soundElem.sourceUrl)!;
    }
    if (msg.quoteElem?.quoteMessage) {
        upgradeMessageUrls(msg.quoteElem.quoteMessage);
    }
    return msg;
}

function getSdkErrorMessage(error: unknown, fallback: string): string {
    if (typeof error === 'string' && error.trim()) return error.trim();
    if (error instanceof Error && error.message.trim()) return error.message.trim();

    if (error && typeof error === 'object') {
        const record = error as Record<string, unknown>;
        const candidates = [record.errMsg, record.message, record.error, record.reason];
        for (const candidate of candidates) {
            if (typeof candidate === 'string' && candidate.trim()) {
                return candidate.trim();
            }
        }
    }

    return fallback;
}

function withTimeout(promise: Promise<unknown>, timeoutMs: number, timeoutMessage: string): Promise<any> {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const timeoutPromise = new Promise<never>((_, reject) => {
        timeoutId = setTimeout(() => {
            reject(new Error(timeoutMessage));
        }, timeoutMs);
    });

    return Promise.race([promise, timeoutPromise]).finally(() => {
        if (timeoutId) clearTimeout(timeoutId);
    });
}

function markMessageAsFailed(message: MessageDto, error: unknown, fallback: string): MessageDto {
    return {
        ...message,
        status: MessageStatus.Failed,
        sendError: getSdkErrorMessage(error, fallback),
    };
}

function getMessageUpdateSignature(message: MessageDto): string {
    return JSON.stringify({
        status: message.status,
        serverMsgID: message.serverMsgID,
        seq: message.seq,
        isRead: message.isRead,
        readAt: message.readAt,
        contentType: message.contentType,
        ex: message.ex,
        localEx: message.localEx,
        sendError: message.sendError,
        attachedInfo: message.attachedInfo,
        textElem: message.textElem,
        atTextElem: message.atTextElem,
        pictureElem: message.pictureElem,
        videoElem: message.videoElem,
        fileElem: message.fileElem,
        soundElem: message.soundElem,
        locationElem: message.locationElem,
        quoteElem: message.quoteElem,
    });
}

function getMessageConversationKey(message: MessageDto): string {
    return message.groupID || message.recvID || '';
}

function getMessageIdentityKeys(message: MessageDto): string[] {
    const keys: string[] = [];

    if (message.clientMsgID && !message.clientMsgID.startsWith('failed_')) {
        keys.push(`client:${message.clientMsgID}`);
    }
    if (message.serverMsgID) {
        keys.push(`server:${message.serverMsgID}`);
    }
    const conversationKey = getMessageConversationKey(message);
    if (conversationKey && typeof message.seq === 'number' && message.seq > 0) {
        keys.push(`seq:${conversationKey}:${message.seq}`);
    }

    return keys;
}

function findMessageIndexByIdentity(messages: MessageDto[], incoming: MessageDto): number {
    const incomingKeys = new Set(getMessageIdentityKeys(incoming));
    if (incomingKeys.size === 0) {
        return -1;
    }

    return messages.findIndex(message =>
        getMessageIdentityKeys(message).some(key => incomingKeys.has(key))
    );
}

function upsertMessageByClientID(messages: MessageDto[], incoming: MessageDto): MessageDto[] {
    const index = findMessageIndexByIdentity(messages, incoming);
    if (index === -1) return [...messages, upgradeMessageUrls(incoming)];

    const existing = messages[index];
    if (
        existing.status === MessageStatus.Failed &&
        incoming.status !== MessageStatus.Succeeded &&
        !incoming.serverMsgID
    ) {
        return messages;
    }

    const merged: MessageDto = {
        ...existing,
        ...incoming,
        isRead: existing.isRead || incoming.isRead,
        readAt: existing.readAt || incoming.readAt,
        sendError: incoming.status === MessageStatus.Failed
            ? (incoming.sendError || existing.sendError)
            : incoming.sendError,
    };

    if (getMessageUpdateSignature(existing) === getMessageUpdateSignature(merged)) {
        return messages;
    }

    const next = [...messages];
    next[index] = upgradeMessageUrls(merged);
    return next;
}

function dedupeMessagesByIdentity(messages: MessageDto[]): MessageDto[] {
    return messages.reduce(
        (next, message) => upsertMessageByClientID(next, message),
        [] as MessageDto[]
    );
}

function normalizeMemberType(rawType: unknown): string {
    const normalized = typeof rawType === 'string' ? rawType.trim().toLowerCase() : '';
    if (
        normalized === 'sale' ||
        normalized === 'salerep' ||
        normalized === 'sale_rep' ||
        normalized === 'sale-rep' ||
        normalized === 'sale rep' ||
        normalized === 'sales' ||
        normalized === 'salesrep' ||
        normalized === 'sales_rep' ||
        normalized === 'sales-rep' ||
        normalized === 'sales rep' ||
        normalized === 'salesman' ||
        normalized === 'salesperson' ||
        normalized === 'sales_representative' ||
        normalized === 'sales-representative' ||
        normalized === 'sales representative'
    ) {
        return 'salesrep';
    }
    if (
        normalized === 'customer' ||
        normalized === 'client' ||
        normalized === 'consumer' ||
        normalized === 'buyer'
    ) return 'customer';
    return normalized || 'customer';
}

function isSalesRepType(rawType: string | undefined): boolean {
    const normalized = normalizeMemberType(rawType);
    return normalized === 'salesrep';
}

function parseBooleanLike(rawValue: unknown): boolean | null {
    if (typeof rawValue === 'boolean') return rawValue;

    if (typeof rawValue === 'number') {
        if (rawValue === 1) return true;
        if (rawValue === 0) return false;
        return null;
    }

    if (typeof rawValue !== 'string') return null;

    const normalized = rawValue.trim().toLowerCase();
    if (!normalized) return null;

    if (['1', 'true', 'yes', 'y'].includes(normalized)) return true;
    if (['0', 'false', 'no', 'n'].includes(normalized)) return false;

    return null;
}

function resolveMemberTypeFromExData(exData: Record<string, unknown>): string {
    const explicitTypeCandidates = [
        exData.type,
        exData.userType,
        exData.user_type,
        exData.memberType,
        exData.member_type,
        exData.role,
    ];

    for (const candidate of explicitTypeCandidates) {
        if (typeof candidate !== 'string' || candidate.trim() === '') continue;
        return normalizeMemberType(candidate);
    }

    const salesFlags = [
        exData.isSalesman,
        exData.isSalesRep,
        exData.isSalesRepresentative,
        exData.salesman,
        exData.salesRep,
        exData.salesRepresentative,
        exData.isSales,
    ];

    for (const flag of salesFlags) {
        if (parseBooleanLike(flag) === true) return 'salesrep';
    }

    const customerFlags = [
        exData.isCustomer,
        exData.isClient,
        exData.customer,
        exData.client,
    ];

    for (const flag of customerFlags) {
        if (parseBooleanLike(flag) === true) return 'customer';
    }

    return 'customer';
}

function receiptAppliesToMessage(
    receipt: { timestamp: number; messageIds?: string[] },
    message: MessageDto,
    currentUserID: string
): boolean {
    if (message.sendID !== currentUserID) return false;

    // Prefer explicit IDs from the read receipt to avoid client clock drift
    // causing the newest outgoing message to remain in "Delivered" state.
    if (Array.isArray(receipt.messageIds) && receipt.messageIds.length > 0) {
        if (!!message.clientMsgID && receipt.messageIds.includes(message.clientMsgID)) return true;
        if (!!message.serverMsgID && receipt.messageIds.includes(message.serverMsgID)) return true;
        if (typeof message.seq === 'number' && receipt.messageIds.includes(String(message.seq))) return true;
    }

    return typeof receipt.timestamp === 'number' && message.sendTime <= receipt.timestamp;
}

function normalizeReceiptTimestamp(rawValue: unknown, fallbackTimestamp?: number): number | null {
    const timestampNumber = typeof rawValue === 'number' ? rawValue : Number(rawValue);
    if (Number.isFinite(timestampNumber) && timestampNumber > 0) {
        return timestampNumber < 1e12 ? timestampNumber * 1000 : timestampNumber;
    }

    if (typeof fallbackTimestamp === 'number' && fallbackTimestamp > 0) {
        return fallbackTimestamp < 1e12 ? fallbackTimestamp * 1000 : fallbackTimestamp;
    }

    return null;
}

interface ReadReceiptParseOptions {
    allowWithoutAction?: boolean;
    defaultSenderId?: string;
    fallbackTimestamp?: number;
}

function parseReadReceiptPayload(
    customData: unknown,
    options: ReadReceiptParseOptions = {}
): { senderId: string; timestamp: number; messageIds: string[] } | null {
    const { allowWithoutAction = false, defaultSenderId = '', fallbackTimestamp } = options;
    let payload: Record<string, unknown> | null = null;

    if (typeof customData === 'string') {
        try {
            const parsed = JSON.parse(customData);
            if (typeof parsed === 'object' && parsed !== null) {
                payload = parsed as Record<string, unknown>;
            }
        } catch {
            payload = null;
        }
    } else if (typeof customData === 'object' && customData !== null) {
        payload = customData as Record<string, unknown>;
    }

    if (!payload) return null;

    // Some clients wrap the receipt payload under "data".
    if (payload.action === undefined && payload.data !== undefined) {
        const nested = parseReadReceiptPayload(payload.data, options);
        if (nested) return nested;
    }

    const rawAction = payload.action;
    const action = typeof rawAction === 'string' ? rawAction.trim().toLowerCase() : '';
    const actionSet = new Set(['read', 'read_status', 'readstatus', 'read-receipt', 'readreceipt']);
    if (action) {
        if (!actionSet.has(action)) return null;
    } else if (!allowWithoutAction) {
        return null;
    }

    const senderIdRaw =
        payload.sender_id ??
        payload.customer_id ??
        payload.senderId ??
        payload.user_id ??
        payload.userID ??
        payload.reader_id ??
        payload.readerId;
    const senderId =
        typeof senderIdRaw === 'string'
            ? senderIdRaw.trim()
            : (senderIdRaw !== undefined && senderIdRaw !== null ? String(senderIdRaw).trim() : defaultSenderId.trim());
    if (!senderId) return null;

    const timestampRaw = payload.timestamp ?? payload.read_at ?? payload.readAt;
    const normalizedTimestamp = normalizeReceiptTimestamp(timestampRaw, fallbackTimestamp) || Date.now();

    const rawMessageIds =
        payload.message_ids ??
        payload.messageIds ??
        payload.client_msg_ids ??
        payload.msg_ids ??
        payload.message_id ??
        payload.messageId;
    const messageIds = Array.isArray(rawMessageIds)
        ? rawMessageIds
            .map(id => (id === undefined || id === null ? '' : String(id).trim()))
            .filter(id => id.length > 0)
        : (rawMessageIds !== undefined && rawMessageIds !== null && String(rawMessageIds).trim().length > 0
            ? [String(rawMessageIds).trim()]
            : []);

    if (messageIds.length === 0) {
        const altIds = payload.seqs ?? payload.seq_list;
        if (Array.isArray(altIds)) {
            altIds.forEach(seq => {
                const normalizedSeq = seq === undefined || seq === null ? '' : String(seq).trim();
                if (normalizedSeq.length > 0) {
                    messageIds.push(normalizedSeq);
                }
            });
        }
    }

    return { senderId, timestamp: normalizedTimestamp, messageIds };
}

function isReadStatusExtension(extension: string | undefined): boolean {
    const normalized = (extension || '').trim().toLowerCase();
    return normalized === 'read_status' || normalized === 'readstatus' || normalized === 'read-receipt' || normalized === 'readreceipt';
}

function extractReadReceiptFromCustomMessage(message: MessageDto): { senderId: string; timestamp: number; messageIds: string[] } | null {
    if (message.contentType !== MessageType.CustomMessage || !message.customElem) return null;

    const isReadStatus = isReadStatusExtension(message.customElem.extension);
    const parseOptions: ReadReceiptParseOptions = {
        allowWithoutAction: isReadStatus,
        defaultSenderId: message.sendID || '',
        fallbackTimestamp: message.sendTime,
    };

    const fromData = parseReadReceiptPayload(message.customElem.data, parseOptions);
    if (fromData) return fromData;

    const fromDescription = parseReadReceiptPayload(message.customElem.description, parseOptions);
    if (fromDescription) return fromDescription;

    if (isReadStatus && parseOptions.defaultSenderId) {
        return {
            senderId: parseOptions.defaultSenderId,
            timestamp: parseOptions.fallbackTimestamp || Date.now(),
            messageIds: [],
        };
    }

    return null;
}

function shouldApplySalesReadReceipt(
    senderId: string,
    currentUserID: string,
    members: GroupMemberDto[] | undefined
): boolean {
    if (!senderId || senderId === currentUserID) return false;

    const senderMember = members?.find(member => member.userID === senderId);
    if (!senderMember) {
        // Member list can be stale/incomplete; don't drop valid receipts from other users.
        return true;
    }

    // If role metadata is missing, still accept receipt from non-self sender.
    if (!senderMember.type || senderMember.type.trim() === '') return true;

    return isSalesRepType(senderMember.type);
}

function parseObjectPayload(rawValue: unknown): Record<string, unknown> | null {
    if (typeof rawValue === 'string') {
        try {
            const parsed = JSON.parse(rawValue);
            return typeof parsed === 'object' && parsed !== null
                ? parsed as Record<string, unknown>
                : null;
        } catch {
            return null;
        }
    }

    return typeof rawValue === 'object' && rawValue !== null
        ? rawValue as Record<string, unknown>
        : null;
}

function getAttachedInfoElem(message: MessageDto): Record<string, unknown> | null {
    const direct = parseObjectPayload(message.attachedInfoElem);
    if (direct) return direct;

    const attachedInfo = parseObjectPayload(message.attachedInfo);
    if (!attachedInfo) return null;

    const nested = parseObjectPayload(attachedInfo.attachedInfoElem);
    if (nested) return nested;

    return attachedInfo.groupHasReadInfo ? attachedInfo : null;
}

function getSalesReadTimestampFromSdkMessage(
    message: MessageDto,
    currentUserID: string,
    members: GroupMemberDto[] | undefined
): number | null {
    if (message.sendID !== currentUserID) return null;

    const attachedInfoElem = getAttachedInfoElem(message);
    const groupHasReadInfo = parseObjectPayload(attachedInfoElem?.groupHasReadInfo);
    if (!groupHasReadInfo) return null;

    const readUserIDs = Array.isArray(groupHasReadInfo.hasReadUserIDList)
        ? groupHasReadInfo.hasReadUserIDList
            .map(userID => (userID === undefined || userID === null ? '' : String(userID).trim()))
            .filter(Boolean)
        : [];

    if (readUserIDs.length === 0) return null;

    const hasSalesReader = readUserIDs.some(userID =>
        shouldApplySalesReadReceipt(userID, currentUserID, members)
    );
    if (!hasSalesReader) return null;

    return normalizeReceiptTimestamp(attachedInfoElem?.hasReadTime, message.readAt) || Date.now();
}

function isAlbumChildMessage(message: MessageDto): boolean {
    if (message.contentType !== MessageType.PictureMessage || !message.ex) return false;

    try {
        const exData = JSON.parse(message.ex);
        return !!exData?.albumID;
    } catch {
        return false;
    }
}

const READ_RECEIPT_CACHE_STORAGE_KEY = 'jfc_chat_sales_read_receipts';
const MAX_CACHED_READ_RECEIPTS_PER_ROOM = 2000;

type ReadReceiptCache = Record<string, Record<string, number>>;

function getReadReceiptCacheRoomKey(currentUserID: string, groupID: string | undefined): string {
    return currentUserID && groupID ? `${currentUserID}:${groupID}` : '';
}

function loadReadReceiptCache(): ReadReceiptCache {
    if (typeof window === 'undefined') return {};

    try {
        const stored = localStorage.getItem(READ_RECEIPT_CACHE_STORAGE_KEY);
        const parsed = stored ? JSON.parse(stored) : {};
        return typeof parsed === 'object' && parsed !== null ? parsed : {};
    } catch {
        return {};
    }
}

function saveReadReceiptCache(cache: ReadReceiptCache): void {
    if (typeof window === 'undefined') return;

    try {
        localStorage.setItem(READ_RECEIPT_CACHE_STORAGE_KEY, JSON.stringify(cache));
    } catch {
        // Cache failures should not affect chat behavior.
    }
}

function rememberSalesReadReceipts(
    currentUserID: string,
    groupID: string | undefined,
    messageIds: string[],
    timestamp: number
): void {
    const roomKey = getReadReceiptCacheRoomKey(currentUserID, groupID);
    if (!roomKey || messageIds.length === 0) return;

    const cache = loadReadReceiptCache();
    const roomCache = cache[roomKey] || {};
    messageIds.forEach(messageId => {
        if (messageId) roomCache[messageId] = timestamp;
    });

    const trimmedEntries = Object.entries(roomCache)
        .sort((a, b) => b[1] - a[1])
        .slice(0, MAX_CACHED_READ_RECEIPTS_PER_ROOM);
    cache[roomKey] = Object.fromEntries(trimmedEntries);
    saveReadReceiptCache(cache);
}

function getCachedSalesReadAt(
    message: MessageDto,
    currentUserID: string,
    fallbackGroupID?: string
): number | null {
    if (message.sendID !== currentUserID) return null;

    const groupID = message.groupID || fallbackGroupID;
    const roomKey = getReadReceiptCacheRoomKey(currentUserID, groupID);
    if (!roomKey) return null;

    const roomCache = loadReadReceiptCache()[roomKey];
    if (!roomCache) return null;

    const cachedReadAt =
        (message.clientMsgID ? roomCache[message.clientMsgID] : undefined) ??
        (message.serverMsgID ? roomCache[message.serverMsgID] : undefined) ??
        (typeof message.seq === 'number' ? roomCache[String(message.seq)] : undefined);

    return normalizeReceiptTimestamp(cachedReadAt) || null;
}

const INCOMING_NOTIFICATION_DEDUP_WINDOW_MS = 8000;
const INCOMING_NOTIFICATION_MAX_CACHE_SIZE = 500;
const INCOMING_SOUND_DEDUP_WINDOW_MS = 700;
const GROUP_FILTER_RETRY_DELAYS_MS = [0, 500, 1500, 3000];

function getIncomingNotificationKey(message: MessageDto): string {
    if (message.clientMsgID) return `client:${message.clientMsgID}`;
    if (message.serverMsgID) return `server:${message.serverMsgID}`;
    if (typeof message.seq === 'number') return `seq:${message.seq}`;
    return `fallback:${message.sendID || ''}:${message.groupID || ''}:${message.sendTime}:${message.contentType}`;
}

// ============================================
// Module-level SDK Singleton Cache
// ============================================
// This ensures getSDK() is only called once across all React re-renders
// The SDK internally logs warnings when initialized multiple times
let cachedSDK: any = null;
let sdkInitPromise: Promise<any> | null = null;
let loginInProgress: Promise<boolean> | null = null;
let loginInProgressUserID: string | null = null;
let lastLoginUserID: string | null = null;

async function getSDKLoginUserID(sdk: any, operationID: string): Promise<string | null> {
    if (!sdk || typeof sdk.getLoginUserID !== 'function') {
        return lastLoginUserID;
    }

    try {
        const result = await sdk.getLoginUserID(operationID);
        const data = result?.data;
        const loginUserID = typeof data === 'string'
            ? data.trim()
            : (data !== undefined && data !== null ? String(data).trim() : '');
        return loginUserID || null;
    } catch {
        return null;
    }
}

async function logoutSDKSession(sdk: any, operationID: string): Promise<void> {
    try {
        await sdk.logout(operationID);
    } catch (e) {
        console.warn('SDK logout failed while switching chat user:', e);
    }

    lastLoginUserID = null;
    await new Promise(resolve => setTimeout(resolve, 300));
}

async function assertSDKAccount(
    sdk: any,
    expectedUserID: string,
    operationID: string
): Promise<void> {
    const activeUserID = await getSDKLoginUserID(sdk, operationID);
    if (activeUserID && activeUserID !== expectedUserID) {
        throw new Error(`OpenIM SDK is logged in as ${activeUserID}, expected ${expectedUserID}`);
    }
}

async function getOrCreateSDK(): Promise<any> {
    // Return cached SDK if available
    if (cachedSDK) {
        return cachedSDK;
    }

    // If initialization is in progress, wait for it
    if (sdkInitPromise) {
        return sdkInitPromise;
    }

    // Initialize SDK once
    sdkInitPromise = (async () => {
        const { getSDK, CbEvents } = await import('@openim/wasm-client-sdk');
        const sdk = getSDK({
            coreWasmPath: '/openIM.wasm',
            sqlWasmPath: '/sql-wasm.wasm',
            debug: false
        }) as any;

        // Store CbEvents on sdk for later use
        sdk._CbEvents = CbEvents;

        // Wait for WASM to be fully initialized
        if (sdk.wasmInitializedPromise) {
            await sdk.wasmInitializedPromise;
        }

        cachedSDK = sdk;
        return sdk;
    })();

    return sdkInitPromise;
}

/**
 * Wrapper for SDK login that prevents concurrent login attempts
 * Returns existing login promise if one is already in progress for the same user
 */
async function loginToSDK(
    sdk: any,
    loginConfig: any,
    operationID: string
): Promise<boolean> {
    const userID = loginConfig.userID;
    const activeUserID = await getSDKLoginUserID(sdk, `${operationID}_active_user`);
    const knownUserID = activeUserID || lastLoginUserID;

    // If login is already in progress for same user, return existing promise
    if (loginInProgressUserID === userID && loginInProgress) {
        console.log('Login already in progress for same user, waiting for existing login...');
        return loginInProgress;
    }

    // If login is in progress for different user, wait for it to complete first
    if (loginInProgress) {
        console.log('Waiting for existing login to complete before starting new login...');
        try {
            await loginInProgress;
        } catch (e) {
            // Ignore errors from previous login attempt
        }

        if (lastLoginUserID === userID) {
            return true;
        }
    }

    // The SDK singleton can survive React/widget remounts. If it is still
    // authenticated as another email-derived OpenIM user, explicitly log out
    // before starting the new account session.
    if (knownUserID && knownUserID !== userID) {
        console.log('Switching SDK user, logging out previous chat session...');
        await logoutSDKSession(sdk, `${operationID}_switch_logout`);
    } else if (knownUserID === userID) {
        lastLoginUserID = userID;
    }

    // Start new login
    loginInProgressUserID = userID;
    loginInProgress = (async () => {
        try {
            await sdk.login(loginConfig, operationID);
            await assertSDKAccount(sdk, userID, `${operationID}_verify_login`);
            lastLoginUserID = userID;
            return true;
        } catch (e: any) {
            // 10102 means the singleton is already logged in. Only accept that
            // when it is known to be the same account; otherwise clear stale SDK
            // auth and retry once with the requested user.
            if (e?.errCode === 10102) {
                const loggedInUserID = await getSDKLoginUserID(sdk, `${operationID}_already_logged_user`);
                if (loggedInUserID === userID || (!loggedInUserID && lastLoginUserID === userID)) {
                    lastLoginUserID = userID;
                    return true;
                }

                await logoutSDKSession(sdk, `${operationID}_retry_logout`);
                await sdk.login(loginConfig, `${operationID}_retry`);
                await assertSDKAccount(sdk, userID, `${operationID}_verify_retry`);
                lastLoginUserID = userID;
                return true;
            }

            lastLoginUserID = null;
            throw e;
        } finally {
            loginInProgress = null;
            loginInProgressUserID = null;
        }
    })();

    return loginInProgress;
}

// ============================================
// Context Types
// ============================================

export type ConnectionStatus = 'Disconnected' | 'Connecting...' | 'Connected' | 'Connection Failed' | 'Kicked Offline' | 'Token Expired';
const TYPING_FAILSAFE_MS = 8000;

interface ChatContextType {
    // SDK & Connection
    imsdk: any;
    isLogged: boolean;
    connectionStatus: ConnectionStatus;
    currentUserID: string;
    currentEmail: string; // User's email address
    userType: UserType;
    jfcToken: string; // JFC authentication token

    // UI State
    layoutState: LayoutState;
    positionState: PositionState;
    setLayoutState: (state: LayoutState) => void;
    setPositionState: (state: PositionState) => void;

    // Rooms & Conversations
    rooms: RoomDto[];
    selectedRoom: RoomDto | null;
    conversations: ConversationDto[];
    selectRoom: (room: RoomDto | null) => void;
    getRoomUnreadCount: (room: RoomDto | null | undefined) => number;

    // Messages
    messages: MessageDto[];
    loadMessages: (conversationID: string, lastMsgID?: string, count?: number) => Promise<MessageDto[]>;
    sendTextMessage: (params: {
        text: string;
        groupID: string;
        requestId?: string;
        atUserIDList?: string[];
        atUsersInfo?: AtUsersInfoItem[];
    }) => Promise<MessageDto | null>;
    sendImageMessage: (params: { file: File; groupID: string; requestId?: string; albumId?: string }) => Promise<MessageDto | null>;
    sendVideoMessage: (params: { file: File; groupID: string; thumbnail: File; duration: number; requestId?: string }) => Promise<MessageDto | null>;
    sendFileMessage: (params: { file: File; groupID: string; requestId?: string }) => Promise<MessageDto | null>;
    sendSoundMessage: (params: { file: File; groupID: string; duration: number; requestId?: string }) => Promise<MessageDto | null>;
    sendLocationMessage: (params: { latitude: number; longitude: number; description: string; groupID: string; requestId?: string }) => Promise<MessageDto | null>;
    sendReplyMessage: (params: { text: string; replyItem: MessageDto; groupID: string; requestId?: string }) => Promise<MessageDto | null>;
    sendAlbumMessage: (params: { files: File[]; groupID: string; onProgress?: (current: number, total: number) => void }) => Promise<MessageDto[] | null>;
    revokeMessage: (params: { clientMsgID: string; conversationID: string }) => Promise<boolean>;
    deleteMessageForMe: (params: { clientMsgID: string; conversationID: string }) => Promise<boolean>;
    sendReaction: (params: { emoji: string; messageId: string; groupID: string }) => Promise<boolean>;
    searchMessages: (keyword: string, options?: {
        conversationID?: string;
        count?: number;
        pageIndex?: number;
        messageTypeList?: number[];
        searchTimePosition?: number;
        searchTimePeriod?: number;
    }) => Promise<SearchResultItem[]>;

    // Reactions
    messageReactions: MessageReactions;

    // Typing
    typingStatus: { [conversationID: string]: TypingStatus };
    sendTypingStatus: (conversationID: string, isTyping?: boolean) => void;

    // Group Management
    openGroup: (groupID: string) => Promise<void>;
    closeGroup: (groupID: string) => Promise<void>;
    markAsRead: (conversationID: string) => Promise<void>;

    // Image & Video Viewers
    imageViewer: ImageViewerData | null;
    videoViewer: VideoViewerData | null;
    openImageViewer: (images: string[], currentIndex: number) => void;
    openVideoViewer: (urls: string[], currentIndex: number) => void;
    closeImageViewer: () => void;
    closeVideoViewer: () => void;
    nextImage: () => void;
    prevImage: () => void;
    nextVideo: () => void;
    prevVideo: () => void;

    // Initialization
    init: (userID: string, jfcToken: string, userInfo?: Partial<UserDto>, groupID?: string, groupName?: string, customerNo?: string) => Promise<boolean>;
    logout: () => Promise<void>;
    updateGroupFilter: (groupID: string | null, groupName?: string | null, customerNo?: string) => Promise<void>;

    // Membership
    isActiveMember: boolean;
    checkMembership: (groupID: string) => Promise<boolean>;
}

// ============================================
// Context Creation
// ============================================

const ChatContext = createContext<ChatContextType | null>(null);

export function useChatContext() {
    const context = useContext(ChatContext);
    if (!context) {
        throw new Error('useChatContext must be used within a ChatProvider');
    }
    return context;
}

// ============================================
// Provider Component
// ============================================

interface ChatProviderProps {
    children: React.ReactNode;
}

export function ChatProvider({ children }: ChatProviderProps) {
    const { t } = useLanguage();

    // SDK & Connection State
    const [imsdk, setImsdk] = useState<any>(null);

    const [isLogged, setIsLogged] = useState(false);

    const getLocalizedMessagePreviewText = useCallback((message: MessageDto | null | undefined) => {
        return getMessagePreviewText(message, {
            newMessage: t.newMessage,
            customMessage: t.customMessage,
            sticker: t.replySticker,
            image: t.image,
            video: t.video,
            file: t.file,
            voice: t.voice,
            location: t.location,
            replyMessage: t.replyMessage,
            thisMessageWasDeleted: t.thisMessageWasDeleted,
        });
    }, [t]);



    // ============================================
    // Search Messages
    // ============================================

    const searchMessages = useCallback(async (
        keyword: string,
        options?: {
            conversationID?: string;
            count?: number;
            pageIndex?: number;
            messageTypeList?: number[];
            searchTimePosition?: number;
            searchTimePeriod?: number;
        }
    ): Promise<SearchResultItem[]> => {
        if (!imsdk || !isLogged || !keyword.trim()) return [];

        try {
            const operationID = Date.now().toString();
            console.log(`Searching for "${keyword}"...`);

            const {
                conversationID = '',
                count = 100,
                pageIndex = 1,
                messageTypeList = [
                    MessageType.TextMessage,
                    MessageType.AdvancedTextMessage,
                    MessageType.AtTextMessage,
                    MessageType.QuoteMessage,
                    MessageType.FileMessage,
                ],
                searchTimePosition = 0,
                searchTimePeriod = 0,
            } = options || {};

            const params = {
                keywordList: [keyword],
                messageTypeList: messageTypeList.length > 0 ? messageTypeList : [MessageType.TextMessage],
                conversationID,
                searchTimePosition,
                searchTimePeriod,
                pageIndex,
                count,
            };

            const result = await imsdk.searchLocalMessages(params, operationID);

            // SDK might return result without errCode on success
            if (result.errCode !== undefined && result.errCode !== 0) {
                console.error('Search failed:', result.errMsg);
                return [];
            }

            let searchResult = result.data;
            if (typeof searchResult === 'string') {
                searchResult = JSON.parse(searchResult);
            }

            if (!searchResult || !searchResult.searchResultItems) {
                return [];
            }

            return searchResult.searchResultItems;
        } catch (error) {
            console.error('Error searching messages:', error);
            return [];
        }
    }, [imsdk, isLogged]);
    const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('Disconnected');
    const [currentUserID, setCurrentUserID] = useState('');
    const [currentEmail, setCurrentEmail] = useState('');
    const [jfcToken, setJfcToken] = useState('');

    // Ref to track the last message ID notified to the backend to prevent duplicates
    // Initialize from localStorage to persist across browser refreshes
    const lastNotifiedMessageID = useRef<{ [conversationID: string]: string }>(
        typeof window !== 'undefined'
            ? (() => {
                try {
                    const stored = localStorage.getItem('jfc_chat_last_notified_msg_id');
                    return stored ? JSON.parse(stored) : {};
                } catch {
                    return {};
                }
            })()
            : {}
    );
    // Set of all message IDs that have had read receipts sent (persisted in localStorage)
    const sentReadReceiptMsgIds = useRef<Set<string>>(
        typeof window !== 'undefined'
            ? (() => {
                try {
                    const stored = localStorage.getItem('jfc_chat_sent_read_receipts');
                    return stored ? new Set<string>(JSON.parse(stored)) : new Set<string>();
                } catch {
                    return new Set<string>();
                }
            })()
            : new Set<string>()
    );
    // IDs for persisted read_status control messages. Kept separate from SDK
    // group receipts because group receipts can update live UI without being
    // recoverable from message history after account switches.
    const sentPersistentReadReceiptMsgIds = useRef<Set<string>>(
        typeof window !== 'undefined'
            ? (() => {
                try {
                    const stored = localStorage.getItem('jfc_chat_sent_persistent_read_receipts');
                    return stored ? new Set<string>(JSON.parse(stored)) : new Set<string>();
                } catch {
                    return new Set<string>();
                }
            })()
            : new Set<string>()
    );
    // Ref to track the last time we sent read receipts for each conversation (with message count)
    const lastReadReceiptSent = useRef<{ [conversationID: string]: { timestamp: number; messageCount: number } }>({});
    // Ref to prevent concurrent markAsRead calls from sending duplicate read_status messages
    const markAsReadInFlight = useRef<Set<string>>(new Set());
    // Short-lived cache to prevent duplicate visual/sound notifications
    // when the SDK emits the same incoming message through multiple events.
    const recentIncomingNotificationRef = useRef<Map<string, number>>(new Map());
    const lastIncomingSoundAtRef = useRef<number>(0);
    const playIncomingSoundOnce = useCallback(() => {
        const soundNow = Date.now();
        if (soundNow - lastIncomingSoundAtRef.current > INCOMING_SOUND_DEDUP_WINDOW_MS) {
            lastIncomingSoundAtRef.current = soundNow;
            notificationService.playSound();
        }
    }, []);
    const [userType, setUserType] = useState<UserType>(UserType.Customer);
    const sdkRef = useRef<any>(null);
    const initializingRef = useRef<boolean>(false); // Guard against multiple concurrent init calls
    const initGenerationRef = useRef<number>(0); // Tracks init generation to discard stale results
    const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const reconnectAttemptRef = useRef<number>(0);
    const recoveryInProgressRef = useRef<boolean>(false);
    const tokenRefreshIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const latestOpenIMTokenRef = useRef<string>('');
    const latestOpenIMPlatformIDRef = useRef<number>(OpenIMConfig.platformId);

    // UI State - Start with Icon state so only the chat button shows initially
    const [layoutState, setLayoutState] = useState<LayoutState>(LayoutState.Icon);
    const [positionState, setPositionState] = useState<PositionState>(PositionState.Widget);

    // Rooms & Conversations
    const [rooms, setRooms] = useState<RoomDto[]>([]);
    const [selectedRoom, setSelectedRoom] = useState<RoomDto | null>(null);
    const [conversations, setConversations] = useState<ConversationDto[]>([]);

    // Refs for accessing state in closures
    const selectedRoomRef = useRef<RoomDto | null>(null);
    const layoutStateRef = useRef<LayoutState>(LayoutState.Icon);
    const roomsRef = useRef<RoomDto[]>([]);
    const currentUserIDRef = useRef<string>('');

    // Keep refs in sync
    useEffect(() => {
        selectedRoomRef.current = selectedRoom;
    }, [selectedRoom]);

    useEffect(() => {
        layoutStateRef.current = layoutState;
    }, [layoutState]);

    const getRoomUnreadCount = useCallback((room: RoomDto | null | undefined): number => {
        if (!room) return 0;

        const rawUnreadCount = Number(room.conversation?.unreadCount || 0);
        if (!Number.isFinite(rawUnreadCount) || rawUnreadCount <= 0) return 0;

        const roomConversationID = room.conversationID || room.conversation?.conversationID;
        const selectedConversationID = selectedRoom?.conversationID || selectedRoom?.conversation?.conversationID;
        const isSelectedRoom =
            (!!roomConversationID && roomConversationID === selectedConversationID) ||
            (!!room.groupID && room.groupID === selectedRoom?.groupID);
        const isChatOpen = layoutState === LayoutState.Minimize || layoutState === LayoutState.Maximize;

        return isSelectedRoom && isChatOpen ? 0 : rawUnreadCount;
    }, [layoutState, selectedRoom]);

    const clearUnreadCountForConversation = useCallback((conversationID: string) => {
        if (!conversationID) return;

        const clearRoomUnreadCount = (room: RoomDto): RoomDto => {
            const roomConversationID = room.conversationID || room.conversation?.conversationID;
            if (roomConversationID !== conversationID || !room.conversation) return room;
            if (Number(room.conversation.unreadCount || 0) <= 0) return room;

            return {
                ...room,
                conversation: {
                    ...room.conversation,
                    unreadCount: 0,
                },
            };
        };

        const clearConversationUnreadCount = (conversation: ConversationDto): ConversationDto => {
            if (conversation.conversationID !== conversationID) return conversation;
            if (Number(conversation.unreadCount || 0) <= 0) return conversation;

            return {
                ...conversation,
                unreadCount: 0,
            };
        };

        roomsRef.current = roomsRef.current.map(clearRoomUnreadCount);
        selectedRoomRef.current = selectedRoomRef.current
            ? clearRoomUnreadCount(selectedRoomRef.current)
            : selectedRoomRef.current;

        setRooms(prev => prev.map(clearRoomUnreadCount));
        setSelectedRoom(prev => prev ? clearRoomUnreadCount(prev) : prev);
        setConversations(prev => prev.map(clearConversationUnreadCount));
    }, []);

    useEffect(() => {
        roomsRef.current = rooms;

        // Calculate total unread count for current context
        const totalUnread = rooms.reduce((sum, room) => sum + getRoomUnreadCount(room), 0);

        // Dispatch event for host app
        const event = new CustomEvent('jfc-chat:on-unread-count', {
            detail: { count: totalUnread }
        });
        window.dispatchEvent(event);
    }, [rooms, getRoomUnreadCount]);

    useEffect(() => {
        currentUserIDRef.current = currentUserID;
    }, [currentUserID]);

    const isLoggedRef = useRef(false);
    useEffect(() => {
        isLoggedRef.current = isLogged;
    }, [isLogged]);


    // Messages
    const [messages, setMessages] = useState<MessageDto[]>([]);

    useEffect(() => {
        if (!currentUserID) return;

        messages.forEach(message => {
            if (message.sendID !== currentUserID || !message.clientMsgID || !message.readAt) return;
            rememberSalesReadReceipts(
                currentUserID,
                message.groupID || selectedRoomRef.current?.groupID,
                [message.clientMsgID],
                message.readAt
            );
        });
    }, [messages, currentUserID]);

    // Typing Status
    const [typingStatus, setTypingStatus] = useState<{ [conversationID: string]: TypingStatus }>({});
    const typingFailsafeTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

    const clearTypingFailsafeTimer = useCallback((conversationID: string) => {
        const timeoutId = typingFailsafeTimersRef.current.get(conversationID);
        if (timeoutId) {
            clearTimeout(timeoutId);
            typingFailsafeTimersRef.current.delete(conversationID);
        }
    }, []);

    const clearAllTypingFailsafeTimers = useCallback(() => {
        typingFailsafeTimersRef.current.forEach(timeoutId => clearTimeout(timeoutId));
        typingFailsafeTimersRef.current.clear();
    }, []);

    const clearTypingIndicators = useCallback((conversationIDs: string[]) => {
        const uniqueConversationIDs = Array.from(new Set(conversationIDs.filter(Boolean)));
        if (uniqueConversationIDs.length === 0) return;

        uniqueConversationIDs.forEach(clearTypingFailsafeTimer);
        const conversationIDSet = new Set(uniqueConversationIDs);

        setTypingStatus(prev => {
            let changed = false;
            const updated = { ...prev };
            uniqueConversationIDs.forEach(conversationID => {
                if (updated[conversationID]) {
                    delete updated[conversationID];
                    changed = true;
                }
            });
            return changed ? updated : prev;
        });

        setRooms(prev => {
            let changed = false;
            const updated = prev.map(room => {
                if (!room.conversationID || !conversationIDSet.has(room.conversationID)) return room;
                if (!room.typing && !room.typingUserID) return room;
                changed = true;
                return { ...room, typing: false, typingUserID: '' };
            });
            return changed ? updated : prev;
        });
    }, [clearTypingFailsafeTimer]);

    // Message Reactions
    const [messageReactions, setMessageReactions] = useState<MessageReactions>({});

    // Viewers
    const [imageViewer, setImageViewer] = useState<ImageViewerData | null>(null);
    const [videoViewer, setVideoViewer] = useState<VideoViewerData | null>(null);

    // Current account filter (groupID = customerId for OpenIM filtering)
    const [, setCurrentGroupID] = useState<string | null>(null);
    const currentGroupIDRef = useRef<string | null>(null);
    const groupFilterRequestRef = useRef<number>(0);
    const [, setCurrentGroupName] = useState<string | null>(null);
    const currentGroupNameRef = useRef<string | null>(null);
    // customerNo for display in room headers (e.g. "14-0000")
    const currentCustomerNoRef = useRef<string | undefined>(undefined);

    // Store pending read receipts to apply when messages are loaded
    const [, setPendingReadReceipts] = useState<Array<{ senderId: string, timestamp: number, messageIds?: string[] }>>([]);
    const pendingReadReceiptsRef = useRef<Array<{ senderId: string, timestamp: number, messageIds?: string[] }>>([]);

    // Active membership tracking - whether user is a member of the current group
    // Default to false - chat icon is hidden until membership is confirmed
    const [isActiveMember, setIsActiveMember] = useState<boolean>(false);
    const isActiveMemberRef = useRef<boolean>(false);

    // Keep ref in sync and dispatch member status event to host app
    useEffect(() => {
        isActiveMemberRef.current = isActiveMember;

        // Dispatch member status event to host app (jfc-app)
        const event = new CustomEvent('jfc-chat:on-member-status', {
            detail: {
                isMember: isActiveMember,
                groupID: currentGroupIDRef.current,
                timestamp: Date.now()
            },
            bubbles: true,
            composed: true
        });
        window.dispatchEvent(event);
    }, [isActiveMember]);

    // ============================================
    // Create Synthetic Room (for new members with no conversation yet)
    // ============================================

    const createSyntheticRoom = useCallback(async (sdk: any, groupID: string, groupName?: string | null, customerNo?: string): Promise<RoomDto | null> => {
        try {
            const operationID = Date.now().toString();
            const groupResult = await sdk.getSpecifiedGroupsInfo([groupID], operationID);

            if (groupResult?.errCode && groupResult.errCode !== 0) return null;

            const gData = typeof groupResult.data === 'string' ? JSON.parse(groupResult.data) : groupResult.data;
            if (!gData?.[0]) return null;

            const groupInfo = gData[0];

            // Verify membership
            const joinResult = await sdk.isJoinGroup(groupID, operationID);
            if (joinResult?.errCode && joinResult.errCode !== 0) return null;
            const isMember = joinResult.data === true || joinResult.data === 'true';
            if (!isMember) return null;

            // Fetch members
            let members: GroupMemberDto[] = [];
            try {
                const memberResult = await sdk.getGroupMemberList({
                    groupID,
                    filter: 0,
                    offset: 0,
                    count: 1000,
                }, operationID);
                if (!memberResult?.errCode || memberResult.errCode === 0) {
                    members = typeof memberResult.data === 'string' ? JSON.parse(memberResult.data) : memberResult.data || [];
                    members = members.filter((m: any) => m.userID !== 'imAdmin');

                    const memberIDs = members.map((m: any) => m.userID);
                    if (memberIDs.length > 0) {
                        const userInfoResult = await sdk.getUsersInfo(memberIDs, operationID);
                        const userInfos = typeof userInfoResult.data === 'string' ? JSON.parse(userInfoResult.data) : userInfoResult.data;
                        members.forEach((member: GroupMemberDto) => {
                            const userInfo = userInfos?.find((u: any) => u.userID === member.userID);
                            if (userInfo?.ex) {
                                try {
                                    const exData = JSON.parse(userInfo.ex);
                                    member.email = exData.email || '';
                                    member.phone = exData.phone || '';
                                    member.type = resolveMemberTypeFromExData(exData);
                                } catch {
                                    // Ignore parse errors
                                }
                            }
                        });
                    }
                }
            } catch {
                // Continue with empty members
            }

            const displayName = groupInfo.groupName || groupName || groupID;
            const emails = members.filter((m: GroupMemberDto) => m.email).map((m: GroupMemberDto) => m.email).join(', ');

            return {
                groupID,
                groupName: displayName,
                customerNo,
                introduction: groupInfo.introduction || 'open',
                createTime: groupInfo.createTime || 0,
                members,
                emails,
                conversationID: `sg_${groupID}`,
                symbol: getInitials(emails || displayName || ''),
                bgColor: getAvatarColor(displayName || ''),
                typing: false,
                typingUserID: '',
            };
        } catch {
            return null;
        }
    }, []);

    const createFastRoom = useCallback((
        groupID: string,
        groupName?: string | null,
        customerNo?: string
    ): RoomDto => {
        const displayName = groupName || groupID;
        return {
            groupID,
            groupName: displayName,
            customerNo,
            introduction: 'open',
            createTime: Date.now(),
            members: [],
            emails: '',
            conversationID: `sg_${groupID}`,
            symbol: getInitials(displayName),
            bgColor: getAvatarColor(displayName),
            typing: false,
            typingUserID: '',
        };
    }, []);

    const activateRoom = useCallback((room: RoomDto) => {
        roomsRef.current = [room];
        selectedRoomRef.current = room;
        setRooms([room]);
        setSelectedRoom(room);
        setIsActiveMember(true);
    }, []);

    // ============================================
    // Fetch Conversations
    // ============================================

    const fetchConversations = useCallback(async (sdk: any) => {
        try {
            const operationID = Date.now().toString();
            const { data } = await sdk.getAllConversationList(operationID);

            let list = typeof data === 'string' ? JSON.parse(data) : data;

            console.log('[ChatContext] fetchConversations: all groupIDs =', list.map((c: any) => c.groupID), 'filter =', currentGroupIDRef.current);

            // Filter by current account's groupID (customerId) if set
            if (currentGroupIDRef.current) {
                list = list.filter((conv: any) => conv.groupID === currentGroupIDRef.current);
                console.log('[ChatContext] fetchConversations: filtered to', list.length, 'conversations');
            }

            // Enrich with group info and members, and verify membership
            const enrichedListWithNulls = await Promise.all(list.map(async (conv: any) => {
                if (conv.groupID) {
                    try {
                        // First, verify current user is still a member of this group
                        // Use isJoinGroup API to check membership
                        try {
                            const isJoinResult = await sdk.isJoinGroup(conv.groupID, operationID);
                            const isJoined = isJoinResult?.data === true || isJoinResult?.data === 'true';
                            if (!isJoined) {
                                return null; // Mark for removal
                            }
                        } catch (joinCheckError: any) {
                            // If isJoinGroup fails, the user might have been kicked
                            // Continue with other checks
                        }

                        // Get group info
                        const groupResult = await sdk.getSpecifiedGroupsInfo([conv.groupID], operationID);

                        // Check for SDK error
                        if (groupResult?.errCode && groupResult.errCode !== 0) {
                            // If we can't get group info, user might have been kicked
                            if (groupResult.errCode === 1004 || groupResult.errCode === 1005) {
                                return null; // Mark for removal
                            }
                        } else {
                            const gData = typeof groupResult.data === 'string' ? JSON.parse(groupResult.data) : groupResult.data;
                            if (gData?.[0]) {
                                conv.groupInfo = gData[0];
                                conv.introduction = gData[0].introduction || 'open';
                            }
                        }

                        // Get members
                        const memberResult = await sdk.getGroupMemberList({
                            groupID: conv.groupID,
                            filter: 0,
                            offset: 0,
                            count: 1000,
                        }, operationID);

                        // Check for SDK error
                        if (memberResult?.errCode && memberResult.errCode !== 0) {
                            // If we can't get members, user might have been kicked
                            if (memberResult.errCode === 1004 || memberResult.errCode === 1005) {
                                return null; // Mark for removal
                            }
                        } else {
                            let members = typeof memberResult.data === 'string' ? JSON.parse(memberResult.data) : memberResult.data;

                            if (members?.length > 0) {
                                members = members.filter((m: any) => m.userID !== 'imAdmin');

                                // Verify current user is in the member list
                                const currentUserInGroup = members.some((m: any) => m.userID === currentUserIDRef.current);
                                if (!currentUserInGroup) {
                                    return null; // Mark for removal
                                }

                                const memberIDs = members.map((m: any) => m.userID);
                                if (memberIDs.length > 0) {
                                    const userInfoResult = await sdk.getUsersInfo(memberIDs, operationID);
                                    let userInfos = typeof userInfoResult.data === 'string' ? JSON.parse(userInfoResult.data) : userInfoResult.data;

                                    members.forEach((member: GroupMemberDto) => {
                                        const userInfo = userInfos?.find((u: any) => u.userID === member.userID);
                                        if (userInfo?.ex) {
                                            try {
                                                const exData = JSON.parse(userInfo.ex);
                                                member.email = exData.email || '';
                                                member.phone = exData.phone || '';
                                                member.type = resolveMemberTypeFromExData(exData);
                                            } catch {
                                                // Silently ignore parse errors for user ex data
                                            }
                                        }
                                    });
                                }

                                conv.members = members;
                                conv.emails = members.filter((m: GroupMemberDto) => m.email).map((m: GroupMemberDto) => m.email).join(', ');
                            }
                        }
                    } catch (e) {
                        // Log as warning since this is not critical - conversation can still be displayed
                        console.warn(`Could not enrich group ${conv.groupID}:`, e instanceof Error ? e.message : 'Unknown error');
                    }
                }
                return conv;
            }));

            // Filter out null entries (groups where user is no longer a member)
            const enrichedList = enrichedListWithNulls.filter((conv): conv is NonNullable<typeof conv> => conv !== null);

            setConversations(enrichedList || []);

            // Convert to rooms for display
            const roomList: RoomDto[] = enrichedList.map((conv: ConversationDto) => {
                // Prefer groupInfo.groupName (updated by batch program with customer name)
                // over conv.showName (cached conversation display name)
                const displayName = conv.groupInfo?.groupName || conv.showName || conv.groupID || '';
                return {
                groupID: conv.groupID || '',
                groupName: displayName,
                customerNo: currentCustomerNoRef.current,
                introduction: conv.introduction || 'open',
                createTime: conv.latestMsgSendTime || 0,
                members: conv.members || [],
                conversation: conv,
                emails: conv.emails || '',
                conversationID: conv.conversationID,
                symbol: getInitials(conv.emails || displayName || ''),
                bgColor: getAvatarColor(displayName || ''),
                lastMessage: typeof conv.latestMsg === 'string' ? JSON.parse(conv.latestMsg) : conv.latestMsg || null,
                typing: false,
                typingUserID: ''
                };
            });

            const currentSelectedRoom = selectedRoomRef.current;
            const shouldPreserveFastRoom =
                roomList.length === 0 &&
                !!currentGroupIDRef.current &&
                currentSelectedRoom?.groupID === currentGroupIDRef.current;
            const visibleRoomList = shouldPreserveFastRoom && currentSelectedRoom
                ? [currentSelectedRoom]
                : roomList;

            roomsRef.current = visibleRoomList;
            setRooms(visibleRoomList);

            if (currentSelectedRoom) {
                const enrichedSelectedRoom = roomList.find(room => room.groupID === currentSelectedRoom.groupID);
                if (enrichedSelectedRoom) {
                    selectedRoomRef.current = enrichedSelectedRoom;
                    setSelectedRoom(enrichedSelectedRoom);
                } else if (roomList.length > 0 || !currentGroupIDRef.current) {
                    selectedRoomRef.current = null;
                    setSelectedRoom(null);
                    setMessages([]);
                }
            }

            // Return the room list so callers can use it immediately
            return roomList;
        } catch (err) {
            console.error('Failed to fetch conversations:', err);
            return [];
        }
    }, []);

    // ============================================
    // Setup Event Listeners
    // ============================================

    const setupEventListeners = useCallback((sdk: any) => {
        sdk.on('OnConnecting', () => setConnectionStatus('Connecting...'));
        sdk.on('OnConnectSuccess', () => {
            setConnectionStatus('Connected');
            setIsLogged(true);
            reconnectAttemptRef.current = 0;
            if (reconnectTimerRef.current) {
                clearTimeout(reconnectTimerRef.current);
                reconnectTimerRef.current = null;
            }
        });
        sdk.on('OnConnectFailed', (data: any) => {
            console.error('Connection Failed', data);
            setConnectionStatus('Connection Failed');
        });
        sdk.on('OnKickedOffline', () => setConnectionStatus('Kicked Offline'));
        sdk.on('OnUserTokenExpired', () => {
            setIsLogged(false);
            setConnectionStatus('Token Expired');
        });

        // Fetch conversations and refresh messages after sync completes
        // (SDK has finished loading data from server, e.g. after reconnecting from background)
        sdk.on('OnSyncServerFinish', async () => {
            try {
                const fetchedRooms = await fetchConversations(sdk);
                const hasRooms = fetchedRooms.length > 0;
                setIsActiveMember(hasRooms);
                window.dispatchEvent(new CustomEvent('jfc-chat:on-member-status', {
                    detail: {
                        isMember: hasRooms,
                        groupID: currentGroupIDRef.current,
                        reason: 'sync_server_finish',
                        timestamp: Date.now()
                    },
                    bubbles: true,
                    composed: true
                }));
            } catch (e) {
                console.debug('fetchConversations after sync failed (non-critical):', e);
            }

            // Reload messages for the currently selected room to pick up any
            // messages received while the app was backgrounded (especially on iOS
            // where WebSocket connections are killed by the OS)
            const room = selectedRoomRef.current;
            if (room?.conversationID) {
                const operationID = Date.now().toString();
                sdk.getAdvancedHistoryMessageList({
                    conversationID: room.conversationID,
                    startClientMsgID: '',
                    count: 40,
                }, operationID).then((result: any) => {
                    if (!result?.data) return;
                    const { data } = result;
                    let list = data.messageList || [];
                    if (typeof list === 'string') list = JSON.parse(list);

                    // Filter to only displayable messages (same filters as loadMessages)
                    const syncSystemMsgTypes = [
                        MessageType.GroupCreatedNotification,
                        MessageType.GroupMemberMutedNotification,
                        MessageType.MemberEnterNotification,
                        MessageType.MemberInvitedNotification,
                    ];
                    const displayable = list.filter((m: any) => {
                        if (syncSystemMsgTypes.includes(m.contentType)) return false;
                        if (m.contentType === 2101) return false; // Revoke notifications
                        if (m.contentType === MessageType.CustomMessage && m.customElem) {
                            const receipt = extractReadReceiptFromCustomMessage(m as MessageDto);
                            if (receipt) return false;
                        }
                        return true;
                    });

                    if (displayable.length > 0) {
                        setMessages(prev => {
                            return displayable.reduce(
                                (next: MessageDto[], message: MessageDto) => upsertMessageByClientID(next, message),
                                prev
                            );
                        });
                    }
                }).catch(() => {
                    // Non-critical, ignore
                });
            }
        });

        const handleIncomingMessages = (eventPayload: any) => {
            try {
                // Support both event wrappers ({ data }) and direct payloads.
                const rawPayload = eventPayload?.data ?? eventPayload;
                const msg = typeof rawPayload === 'string' ? JSON.parse(rawPayload) : rawPayload;
                if (!msg) return;

                const items: MessageDto[] = (Array.isArray(msg) ? msg : [msg])
                    .filter(Boolean)
                    .map(upgradeMessageUrls);
                // Process each message
                const displayableMessages: MessageDto[] = [];

                items.forEach(item => {
                    // Check for SDK's RevokeMessage type (2101) - this is how revoked messages are received
                    if (item.contentType === 2101) {
                        // Extract the revoked message clientMsgID from the content
                        try {
                            const revokeContent = typeof item.content === 'string' ? JSON.parse(item.content) : item.content;
                            const revokedMsgID = revokeContent?.clientMsgID || revokeContent?.revokedMsgClientID;
                            if (revokedMsgID) {
                                setMessages(prev => prev.map(m =>
                                    m.clientMsgID === revokedMsgID
                                        ? { ...m, contentType: MessageType.RevokeMessage }
                                        : m
                                ));
                            }
                        } catch (e) {
                            // Silently ignore parse errors
                        }
                        return; // Don't add revoke notifications to displayable messages
                    }

                    // Handle custom messages specially
                    if (item.contentType === MessageType.CustomMessage && item.customElem) {
                        const receipt = extractReadReceiptFromCustomMessage(item);
                        if (receipt) {
                            const { senderId, messageIds, timestamp } = receipt;
                            const isSalesReadingOurMessages = shouldApplySalesReadReceipt(
                                senderId,
                                currentUserIDRef.current,
                                selectedRoomRef.current?.members
                            );

                            if (isSalesReadingOurMessages) {
                                rememberSalesReadReceipts(
                                    currentUserIDRef.current,
                                    item.groupID || selectedRoomRef.current?.groupID,
                                    messageIds,
                                    timestamp
                                );
                            }

                            // Store the read receipt for later application
                            setPendingReadReceipts(prev => {
                                const updated = [...prev, { senderId, timestamp, messageIds }];
                                pendingReadReceiptsRef.current = updated;
                                return updated;
                            });

                            // Apply to current messages state immediately
                            setMessages(prev => {
                                // Only mark as read if a sales rep read our messages (not another customer)
                                if (isSalesReadingOurMessages) {
                                    const updatedMessages = prev.map(m => {
                                        if (
                                            receiptAppliesToMessage({ timestamp, messageIds }, m, currentUserIDRef.current) &&
                                            (!m.isRead || !m.readAt)
                                        ) {
                                            return { ...m, isRead: true, readAt: timestamp };
                                        }
                                        return m;
                                    });
                                    return updatedMessages;
                                }
                                // Ignore read receipts from other customers - only sales rep reads matter
                                return prev;
                            });
                        } else {
                            let customData: any = null;
                            if (item.customElem.data) {
                                try {
                                    customData = JSON.parse(item.customElem.data);
                                } catch {
                                    customData = null;
                                }
                            }

                            if (customData?.type === 'reaction') {
                                // Handle reaction
                                const { emoji, messageId, userId } = customData;
                                if (emoji && messageId && userId) {
                                    setMessageReactions(prev => {
                                        const updated = { ...prev };
                                        if (!updated[messageId]) updated[messageId] = {};
                                        if (!updated[messageId][emoji]) updated[messageId][emoji] = [];
                                        if (!updated[messageId][emoji].includes(userId)) {
                                            updated[messageId][emoji] = [...updated[messageId][emoji], userId];
                                        }
                                        return updated;
                                    });
                                }
                                // Keep reaction custom messages in the message stream.
                                displayableMessages.push(item);
                            } else {
                                // Other custom messages (like stickers) should be displayed
                                displayableMessages.push(item);
                            }
                        }
                    } else {
                        // Regular messages - check if they have displayable content
                        const hasContent = !!(
                            (
                                (item.contentType === MessageType.TextMessage ||
                                    item.contentType === MessageType.AtTextMessage ||
                                    item.contentType === MessageType.AdvancedTextMessage) &&
                                (item.textElem?.content || item.atTextElem?.text)
                            ) ||
                            (item.contentType === MessageType.QuoteMessage && item.quoteElem) ||
                            (item.contentType === MessageType.PictureMessage && item.pictureElem) ||
                            (item.contentType === MessageType.VideoMessage && item.videoElem) ||
                            (item.contentType === MessageType.FileMessage && item.fileElem) ||
                            (item.contentType === MessageType.VoiceMessage && item.soundElem) ||
                            (item.contentType === MessageType.LocationMessage && item.locationElem) ||
                            item.contentType === MessageType.RevokeMessage
                        );
                        if (hasContent) {
                            displayableMessages.push(item);
                        }
                    }
                });

                // Add displayable messages to state (with deduplication)
                if (displayableMessages.length > 0) {
                    // Determine which messages need sound/notification BEFORE setMessages
                    // (React 18 batches state updaters, so we can't rely on arrays mutated inside them)
                    const incomingFromOthers = displayableMessages.filter(msg => {
                        if (msg.sendID === currentUserIDRef.current) return false;
                        const isRelevantRoom = roomsRef.current.some(r => r.groupID === msg.groupID);
                        return isRelevantRoom;
                    });

                    // Deduplicate notifications across overlapping SDK events
                    // (OnRecvNewMessages / OnRecvOnlineOnlyMessages / OnRecvOnlineOnlyMessage).
                    const now = Date.now();
                    const notificationCache = recentIncomingNotificationRef.current;
                    notificationCache.forEach((timestamp, key) => {
                        if (now - timestamp > INCOMING_NOTIFICATION_DEDUP_WINDOW_MS) {
                            notificationCache.delete(key);
                        }
                    });

                    const incomingToNotify = incomingFromOthers.filter(msg => {
                        const notificationKey = getIncomingNotificationKey(msg);
                        const lastNotifiedAt = notificationCache.get(notificationKey);
                        if (lastNotifiedAt && now - lastNotifiedAt < INCOMING_NOTIFICATION_DEDUP_WINDOW_MS) {
                            return false;
                        }
                        notificationCache.set(notificationKey, now);
                        return true;
                    });

                    if (notificationCache.size > INCOMING_NOTIFICATION_MAX_CACHE_SIZE) {
                        const ordered = Array.from(notificationCache.entries())
                            .sort((a, b) => b[1] - a[1])
                            .slice(0, INCOMING_NOTIFICATION_MAX_CACHE_SIZE);
                        recentIncomingNotificationRef.current = new Map(ordered);
                    }

                    // Clear typing indicator when actual messages arrive.
                    // This avoids stale "...is typing" UI if stop events are dropped.
                    const typingConversationIDsToClear = new Set<string>();
                    incomingFromOthers.forEach(msg => {
                        if (!msg.groupID) return;
                        const room = roomsRef.current.find(r => r.groupID === msg.groupID);
                        if (room?.conversationID) {
                            typingConversationIDsToClear.add(room.conversationID);
                        }
                    });
                    if (typingConversationIDsToClear.size > 0) {
                        clearTypingIndicators(Array.from(typingConversationIDsToClear));
                    }

                    // Sound for any incoming message from others.
                    // Keep sound independent from toast dedupe so it remains reliable,
                    // while still throttling burst duplicates from overlapping SDK events.
                    if (incomingFromOthers.length > 0) {
                        playIncomingSoundOnce();
                    }

                    // Visual notifications only when NOT viewing that chat
                    incomingToNotify.forEach(msg => {
                        const currentRoomID = selectedRoomRef.current?.groupID;
                        const currentLayout = layoutStateRef.current;
                        const isChatOpen = currentLayout === LayoutState.Minimize || currentLayout === LayoutState.Maximize;
                        const isViewing = isChatOpen && currentRoomID === msg.groupID;
                        const isHidden = typeof document !== 'undefined' ? document.hidden : false;

                        if (isViewing && !isHidden) return;

                        const notificationText = getLocalizedMessagePreviewText(msg);
                        const messageID = msg.clientMsgID || msg.serverMsgID || `${msg.sendID}-${msg.sendTime}`;

                        const event = new CustomEvent('jfc-chat:on-new-message', {
                            detail: {
                                message: notificationText,
                                senderName: msg.senderNickname || 'User',
                                groupID: msg.groupID,
                                senderID: msg.sendID,
                                messageID,
                                // Host app bridge should avoid creating a second notification.
                                handledByWidgetNotification: true,
                            }
                        });
                        window.dispatchEvent(event);

                        if (!isHidden) {
                            // Tab is visible but user is in a different conversation:
                            // show Messenger-style in-app toast
                            window.dispatchEvent(new CustomEvent('jfc-chat:in-app-notification', {
                                detail: {
                                    senderName: msg.senderNickname || 'User',
                                    message: notificationText,
                                    groupID: msg.groupID || '',
                                }
                            }));
                        } else {
                            // Tab is hidden:
                            // On iOS, always prefer background push (FCM + Service Worker)
                            // to prevent duplicate alerts from local + remote notification paths.
                            // On desktop browsers, still show local notifications for hidden tabs.
                            const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';
                            const isIOSDevice = /iPad|iPhone|iPod/i.test(userAgent) ||
                                (typeof navigator !== 'undefined' &&
                                    navigator.platform === 'MacIntel' &&
                                    navigator.maxTouchPoints > 1);
                            const preferPushOnly = isIOSDevice;

                            if (!preferPushOnly) {
                                notificationService.notifyNewMessage({
                                    senderName: msg.senderNickname || 'User',
                                    message: notificationText,
                                    conversationID: msg.groupID || '',
                                    messageID,
                                });
                            }
                        }
                    });

                    // Update state (deduplication inside updater is fine for state)
                    setMessages(prev => {
                        return displayableMessages.reduce(
                            (next, message) => upsertMessageByClientID(next, message),
                            prev
                        );
                    });

                    // Only fetch conversations when there are displayable messages.
                    fetchConversations(sdk);
                }
            } catch (e) {
                console.error('Failed to process incoming messages:', e);
            }
        };
        sdk.on('OnRecvNewMessages', handleIncomingMessages);
        sdk.on('OnRecvOnlineOnlyMessages', handleIncomingMessages);
        sdk.on('OnRecvOnlineOnlyMessage', handleIncomingMessages);
        sdk.on('OnRecvGroupReadReceipt', (eventData: any) => {
            try {
                const payload = typeof eventData?.data === 'string'
                    ? JSON.parse(eventData.data)
                    : (eventData?.data ?? eventData);
                const readInfoList = Array.isArray(payload?.groupMessageReadInfo)
                    ? payload.groupMessageReadInfo
                    : [];
                if (readInfoList.length === 0) return;

                const readClientMsgIDs = new Set<string>();

                readInfoList.forEach((info: any) => {
                    const clientMsgID = typeof info?.clientMsgID === 'string' ? info.clientMsgID : '';
                    if (!clientMsgID) return;

                    const hasEligibleReader = Array.isArray(info?.readMembers)
                        ? info.readMembers.some((member: any) => {
                            const readerID = typeof member?.userID === 'string'
                                ? member.userID
                                : (member?.userID !== undefined && member?.userID !== null ? String(member.userID) : '');
                            if (!readerID || readerID === currentUserIDRef.current || readerID === 'imAdmin') return false;
                            return shouldApplySalesReadReceipt(
                                readerID,
                                currentUserIDRef.current,
                                selectedRoomRef.current?.members
                            );
                        })
                        : Number(info?.hasReadCount || 0) > 0;

                    if (hasEligibleReader) {
                        readClientMsgIDs.add(clientMsgID);
                    }
                });

                if (readClientMsgIDs.size === 0) return;

                const readAt = Date.now();
                const receiptConversationID =
                    (typeof payload?.conversationID === 'string' ? payload.conversationID : '') ||
                    selectedRoomRef.current?.conversationID ||
                    '';
                const receiptGroupID =
                    roomsRef.current.find(room => room.conversationID === receiptConversationID)?.groupID ||
                    selectedRoomRef.current?.groupID;

                rememberSalesReadReceipts(
                    currentUserIDRef.current,
                    receiptGroupID,
                    Array.from(readClientMsgIDs),
                    readAt
                );

                setMessages(prev => prev.map(message => {
                    if (
                        message.sendID === currentUserIDRef.current &&
                        !!message.clientMsgID &&
                        readClientMsgIDs.has(message.clientMsgID)
                    ) {
                        return { ...message, isRead: true, readAt };
                    }
                    return message;
                }));
            } catch (e) {
                console.debug('OnRecvGroupReadReceipt parse failed (non-critical):', e);
            }
        });

        sdk.on('OnConversationChanged', (eventData: any) => {
            try {
                const conversations = typeof eventData.data === 'string' ? JSON.parse(eventData.data) : eventData.data;
                if (Array.isArray(conversations)) {
                    const isHidden = typeof document !== 'undefined' ? document.hidden : false;
                    let hasUnreadIncreaseWhileHidden = false;
                    if (isHidden) {
                        const prevUnreadByConversation = new Map<string, number>();
                        roomsRef.current.forEach(room => {
                            const key = room.conversationID || room.conversation?.conversationID || room.groupID;
                            if (!key) return;
                            prevUnreadByConversation.set(key, Number(room.conversation?.unreadCount || 0));
                        });

                        hasUnreadIncreaseWhileHidden = conversations.some((conv: any) => {
                            const key = conv?.conversationID || conv?.groupID;
                            if (!key) return false;
                            const nextUnread = Number(conv?.unreadCount || 0);
                            const prevUnread = prevUnreadByConversation.get(key);
                            if (prevUnread === undefined) {
                                // New conversation delivered while hidden.
                                return nextUnread > 0;
                            }
                            return nextUnread > prevUnread;
                        });
                    }

                    conversations.forEach((conv: any) => {
                        // Check if latestMsg contains a revoked message (contentType 2101)
                        if (conv.latestMsg) {
                            const latestMsg = typeof conv.latestMsg === 'string' ? JSON.parse(conv.latestMsg) : conv.latestMsg;

                            // If latest message is a revoke notification (contentType 2101)
                            if (latestMsg?.contentType === 2101) {
                                // Extract revoked message ID from content
                                try {
                                    const revokeContent = typeof latestMsg.content === 'string' ? JSON.parse(latestMsg.content) : latestMsg.content;
                                    const revokedMsgID = revokeContent?.clientMsgID;
                                    if (revokedMsgID) {
                                        setMessages(prev => prev.map(m =>
                                            m.clientMsgID === revokedMsgID
                                                ? { ...m, contentType: MessageType.RevokeMessage }
                                                : m
                                        ));
                                    }
                                } catch (e) {
                                    // Silently ignore parse errors
                                }
                            }
                        }
                    });

                    if (hasUnreadIncreaseWhileHidden) {
                        playIncomingSoundOnce();
                    }
                }
            } catch (e) {
                // Silently ignore errors
            }
            fetchConversations(sdk);
        });
        sdk.on('OnNewConversation', () => fetchConversations(sdk));

        // Triggered when the CURRENT USER is added to a new group
        sdk.on('OnJoinedGroupAdded', (eventData: any) => {
            try {
                const data = typeof eventData.data === 'string'
                    ? JSON.parse(eventData.data)
                    : eventData.data;

                const addedGroupID = data?.groupID;

                // Check if this is the group we're currently viewing
                if (addedGroupID && addedGroupID === currentGroupIDRef.current) {
                    setIsActiveMember(true);
                    setConnectionStatus('Connected');

                    // Dispatch event to notify host app (jfc-app) to show bell icon
                    const event = new CustomEvent('jfc-chat:on-member-status', {
                        detail: {
                            isMember: true,
                            groupID: addedGroupID,
                            reason: 'joined_group_added',
                            timestamp: Date.now()
                        },
                        bubbles: true,
                        composed: true
                    });
                    window.dispatchEvent(event);
                }

                // Refresh conversations to include the new group
                fetchConversations(sdk);
            } catch (e) {
                // Error handling OnJoinedGroupAdded
                fetchConversations(sdk);
            }
        });

        // ============================================
        // Group Membership Change Events
        // ============================================

        // Triggered when the CURRENT USER is removed from a group they were in
        // (kicked, voluntarily left, or group was dismissed)
        sdk.on('OnJoinedGroupDeleted', (eventData: any) => {
            try {
                const data = typeof eventData.data === 'string'
                    ? JSON.parse(eventData.data)
                    : eventData.data;

                const removedGroupID = data?.groupID;

                if (removedGroupID && (
                    removedGroupID === currentGroupIDRef.current ||
                    removedGroupID === selectedRoomRef.current?.groupID
                )) {
                    setIsActiveMember(false);
                    setConnectionStatus('Disconnected');

                    // Dispatch event to notify host app (jfc-app) to hide bell icon
                    const event = new CustomEvent('jfc-chat:on-member-status', {
                        detail: {
                            isMember: false,
                            groupID: removedGroupID,
                            reason: 'joined_group_deleted',
                            timestamp: Date.now()
                        },
                        bubbles: true,
                        composed: true
                    });
                    window.dispatchEvent(event);

                    if (selectedRoomRef.current?.groupID === removedGroupID) {
                        setSelectedRoom(null);
                        setMessages([]);
                    }
                }

                fetchConversations(sdk);
            } catch (e) {
                // Error handling OnJoinedGroupDeleted
            }
        });

        // Triggered when ANY member is removed from a group
        // Other group members receive this notification
        // Check if the deleted member is the current user
        sdk.on('OnGroupMemberDeleted', (eventData: any) => {
            try {
                const data = typeof eventData.data === 'string'
                    ? JSON.parse(eventData.data)
                    : eventData.data;

                const groupID = data?.groupID;
                const deletedUserID = data?.userID;

                // Check if the current user was the one deleted
                if (deletedUserID === currentUserIDRef.current && (
                    groupID === currentGroupIDRef.current ||
                    groupID === selectedRoomRef.current?.groupID
                )) {
                    setIsActiveMember(false);
                    setConnectionStatus('Disconnected');

                    // Dispatch event to notify host app (jfc-app) to hide bell icon
                    const event = new CustomEvent('jfc-chat:on-member-status', {
                        detail: {
                            isMember: false,
                            groupID: groupID,
                            reason: 'member_deleted',
                            timestamp: Date.now()
                        },
                        bubbles: true,
                        composed: true
                    });
                    window.dispatchEvent(event);

                    if (selectedRoomRef.current?.groupID === groupID) {
                        setSelectedRoom(null);
                        setMessages([]);
                    }
                }

                // Refresh conversations to update member list
                fetchConversations(sdk);
            } catch (e) {
                // Error handling OnGroupMemberDeleted
            }
        });

        // Triggered when a group is disbanded
        sdk.on('OnGroupDismissed', (eventData: any) => {
            try {
                const data = typeof eventData.data === 'string'
                    ? JSON.parse(eventData.data)
                    : eventData.data;

                const dismissedGroupID = data?.groupID;

                if (dismissedGroupID && (
                    dismissedGroupID === currentGroupIDRef.current ||
                    dismissedGroupID === selectedRoomRef.current?.groupID
                )) {
                    setIsActiveMember(false);
                    setConnectionStatus('Disconnected');

                    // Dispatch event to notify host app (jfc-app) to hide bell icon
                    const event = new CustomEvent('jfc-chat:on-member-status', {
                        detail: {
                            isMember: false,
                            groupID: dismissedGroupID,
                            reason: 'group_dismissed',
                            timestamp: Date.now()
                        },
                        bubbles: true,
                        composed: true
                    });
                    window.dispatchEvent(event);

                    if (selectedRoomRef.current?.groupID === dismissedGroupID) {
                        setSelectedRoom(null);
                        setMessages([]);
                    }
                }

                fetchConversations(sdk);
            } catch (e) {
                // Error handling OnGroupDismissed
            }
        });

        // Handle message revocation from other users
        // The callback receives the full event object: { event, errCode, errMsg, data, operationID }
        const handleRevoke = (eventData: any) => {
            try {
                // Access data from the event object
                const data = eventData?.data;

                // RevokedInfo: { clientMsgID, revokerID, revokerNickname, revokeTime, ... }
                const revokedMsgID = data?.clientMsgID;

                if (revokedMsgID) {
                    setMessages(prev => prev.map(m =>
                        m.clientMsgID === revokedMsgID
                            ? { ...m, contentType: MessageType.RevokeMessage }
                            : m
                    ));
                }
            } catch (e) {
                // Silently ignore errors
            }
        };

        // Register using CbEvents enum if available
        const CbEvents = sdk._CbEvents;
        if (CbEvents?.OnNewRecvMessageRevoked) {
            sdk.on(CbEvents.OnNewRecvMessageRevoked, handleRevoke);
        }
        // Also register with string name as fallback
        sdk.on('OnNewRecvMessageRevoked', handleRevoke);

        sdk.on('OnConversationUserInputStatusChanged', (eventData: any) => {
            try {
                const payload = eventData?.data ?? eventData;
                const data = typeof payload === 'string' ? JSON.parse(payload) : payload;
                if (!data?.conversationID) return;

                const conversationID: string = data.conversationID;
                const userID: string = data.userID || '';
                const platformIDs: number[] = Array.isArray(data.platformIDs) ? data.platformIDs : [];
                const isTyping = platformIDs.length > 0;

                if (isTyping) {
                    setTypingStatus(prev => ({
                        ...prev,
                        [conversationID]: {
                            conversationID,
                            userID,
                            platformIDs
                        }
                    }));

                    setRooms(prev => prev.map(room => {
                        if (room.conversationID !== conversationID) return room;
                        return { ...room, typing: true, typingUserID: userID };
                    }));

                    clearTypingFailsafeTimer(conversationID);
                    const timeoutId = setTimeout(() => {
                        setTypingStatus(prev => {
                            const current = prev[conversationID];
                            if (!current) return prev;
                            if (userID && current.userID !== userID) return prev;
                            const { [conversationID]: _, ...rest } = prev;
                            return rest;
                        });

                        setRooms(prev => prev.map(room => {
                            if (room.conversationID !== conversationID) return room;
                            if (userID && room.typingUserID && room.typingUserID !== userID) return room;
                            if (!room.typing && !room.typingUserID) return room;
                            return { ...room, typing: false, typingUserID: '' };
                        }));

                        typingFailsafeTimersRef.current.delete(conversationID);
                    }, TYPING_FAILSAFE_MS);
                    typingFailsafeTimersRef.current.set(conversationID, timeoutId);
                    return;
                }

                const activeTypingUserID = roomsRef.current.find(room => room.conversationID === conversationID)?.typingUserID || '';
                if (userID && activeTypingUserID && activeTypingUserID !== userID) {
                    return;
                }

                clearTypingFailsafeTimer(conversationID);
                setTypingStatus(prev => {
                    const current = prev[conversationID];
                    if (!current) return prev;
                    if (userID && current.userID && current.userID !== userID) return prev;
                    const { [conversationID]: _, ...rest } = prev;
                    return rest;
                });
                setRooms(prev => prev.map(room => {
                    if (room.conversationID !== conversationID) return room;
                    if (userID && room.typingUserID && room.typingUserID !== userID) return room;
                    if (!room.typing && !room.typingUserID) return room;
                    return { ...room, typing: false, typingUserID: '' };
                }));
            } catch (e) {
                console.debug('Failed to process typing event:', e);
            }
        });
    }, [clearTypingFailsafeTimer, clearTypingIndicators, fetchConversations, getLocalizedMessagePreviewText, playIncomingSoundOnce]);

    /**
     * Build the custom OpenIM message used as a read receipt between clients.
     *
     * Payload shape:
     * - action: "read"
     * - sender_id: current reader user ID
     * - timestamp: client-side read time
     * - message_ids: IDs that were just read in the active conversation
     *
     * The message is sent with extension "read_status" so receivers can detect
     * and apply read state updates without rendering this as a chat bubble.
     */
    const createReadStatusMessage = useCallback(async (
        sdk: any,
        senderID: string,
        messageIds: string[],
        operationID: string
    ) => {
        try {
            if (!senderID || messageIds.length === 0) {
                console.warn('Invalid read status message parameters');
                return null;
            }

            const customData = {
                action: 'read',
                sender_id: senderID,
                // Backward-compatible aliases for mixed clients.
                customer_id: senderID,
                user_id: senderID,
                timestamp: Date.now(),
                message_ids: messageIds,
                client_msg_ids: messageIds,
                msg_ids: messageIds,
                message_id: messageIds[messageIds.length - 1],
                client_msg_id: messageIds[messageIds.length - 1],
            };

            const result = await sdk.createCustomMessage(
                {
                    data: JSON.stringify(customData),
                    extension: 'read_status',
                    // Keep description empty so read receipts are treated as silent control signals.
                    description: ''
                },
                operationID
            );

            if (result?.errCode && result.errCode !== 0) {
                console.error('Failed to create custom message:', result.errMsg);
                return null;
            }

            return typeof result?.data === 'string'
                ? JSON.parse(result.data)
                : result?.data;
        } catch (error) {
            console.error('Error creating read status message:', error);
            return null;
        }
    }, []);

    // ============================================
    // Logout
    // ============================================

    const logout = useCallback(async () => {
        if (reconnectTimerRef.current) {
            clearTimeout(reconnectTimerRef.current);
            reconnectTimerRef.current = null;
        }
        reconnectAttemptRef.current = 0;
        recoveryInProgressRef.current = false;
        latestOpenIMTokenRef.current = '';
        latestOpenIMPlatformIDRef.current = OpenIMConfig.platformId;
        if (tokenRefreshIntervalRef.current) {
            clearInterval(tokenRefreshIntervalRef.current);
            tokenRefreshIntervalRef.current = null;
        }
        clearAllTypingFailsafeTimers();

        if (sdkRef.current) {
            try {
                await sdkRef.current.logout();
            } catch (e) {
                console.warn('Logout failed:', e);
            }
        }
        lastLoginUserID = null;
        loginInProgress = null;
        loginInProgressUserID = null;
        setImsdk(null);
        setIsLogged(false);
        setConnectionStatus('Disconnected');
        setCurrentUserID('');
        currentUserIDRef.current = '';
        setCurrentEmail('');
        setJfcToken('');
        setRooms([]);
        setSelectedRoom(null);
        setMessages([]);
        setTypingStatus({});
        setConversations([]);
        setCurrentGroupID(null);
        currentGroupIDRef.current = null;
        setCurrentGroupName(null);
        currentGroupNameRef.current = null;
        currentCustomerNoRef.current = undefined;
        await notificationService.cleanup();
    }, [clearAllTypingFailsafeTimers]);

    useEffect(() => {
        if (typeof window === 'undefined') return;

        const cleanup = () => logout();
        (window as any).__jfcChatLogout = cleanup;

        return () => {
            if ((window as any).__jfcChatLogout === cleanup) {
                delete (window as any).__jfcChatLogout;
            }
        };
    }, [logout]);

    // ============================================
    // Update Group Filter (without re-initializing SDK)
    // ============================================

    const updateGroupFilter = useCallback(async (groupID: string | null, groupName?: string | null, customerNo?: string): Promise<void> => {
        console.log('[ChatContext] updateGroupFilter: groupID(customerId)=', groupID, 'customerNo=', customerNo);
        const requestID = ++groupFilterRequestRef.current;
        const normalizedGroupID = groupID || null;

        // Update the group filter without re-initializing SDK
        // groupID = customerId, used for OpenIM conversation matching
        currentGroupIDRef.current = normalizedGroupID;
        setCurrentGroupID(normalizedGroupID);
        currentGroupNameRef.current = groupName ?? null;
        setCurrentGroupName(groupName ?? null);
        currentCustomerNoRef.current = customerNo;

        // Clear previous-room state immediately so auto-select cannot pick the
        // prior customer while the new filter is loading.
        roomsRef.current = [];
        selectedRoomRef.current = null;
        setRooms([]);
        setConversations([]);
        setSelectedRoom(null);
        setMessages([]);

        const isCurrentRequest = () =>
            requestID === groupFilterRequestRef.current &&
            currentGroupIDRef.current === normalizedGroupID;

        const dispatchMemberStatusForRequest = (isMember: boolean, reason?: string) => {
            if (!isCurrentRequest()) return;

            setIsActiveMember(isMember);

            const event = new CustomEvent('jfc-chat:on-member-status', {
                detail: {
                    isMember,
                    groupID: normalizedGroupID,
                    reason,
                    timestamp: Date.now()
                },
                bubbles: true,
                composed: true
            });
            window.dispatchEvent(event);
        };

        let hasRooms = false;
        let sdkReadyAtLeastOnce = false;

        for (const delayMs of GROUP_FILTER_RETRY_DELAYS_MS) {
            if (delayMs > 0) {
                await new Promise(resolve => setTimeout(resolve, delayMs));
            }

            if (!isCurrentRequest()) return;

            const sdk = sdkRef.current || imsdk;
            if (!sdk || !isLoggedRef.current) {
                continue;
            }

            sdkReadyAtLeastOnce = true;

            // fetchConversations returns the room list so we can use it immediately
            const fetchedRooms = await fetchConversations(sdk);

            if (!isCurrentRequest()) return;

            // Check if we have rooms for this group
            hasRooms = fetchedRooms && fetchedRooms.length > 0;

            // If no rooms found but groupID is specified, try checking if the group exists directly
            // This handles the case where conversations cache might be stale
            // If no conversations exist but user is a member, create a synthetic room
            // so the user can send messages before any conversation is created
            if (!hasRooms && normalizedGroupID) {
                const syntheticRoom = await createSyntheticRoom(sdk, normalizedGroupID, groupName, customerNo);

                if (!isCurrentRequest()) return;

                if (syntheticRoom) {
                    hasRooms = true;
                    setRooms([syntheticRoom]);
                    setSelectedRoom(syntheticRoom);
                }
            }

            if (hasRooms) {
                break;
            }
        }

        if (hasRooms) {
            dispatchMemberStatusForRequest(true);
            return;
        }

        // Avoid hiding the icon on a transient SDK/cache miss during account
        // switching. Only publish inactive after the SDK was actually available.
        if (sdkReadyAtLeastOnce || !isActiveMemberRef.current) {
            dispatchMemberStatusForRequest(false, sdkReadyAtLeastOnce ? 'group_filter_empty' : 'sdk_not_ready');
        }
    }, [imsdk, isLogged, fetchConversations, createSyntheticRoom]);

    // Listen for group-change events from the widget wrapper
    // This handles both token updates and group filter updates
    useEffect(() => {
        const handleGroupChange = async (event: any) => {
            const { groupID, groupName, customerNo, jfcToken: newToken } = event.detail || {};

            // Update the token if provided
            if (newToken) {
                setJfcToken(newToken);
            }

            // Always update the group filter when group changes
            // This ensures the chat shows the correct group's conversations
            if (groupID) {
                await updateGroupFilter(groupID, groupName || null, customerNo);
            }
        };

        window.addEventListener('jfc-chat:group-change', handleGroupChange);
        return () => {
            window.removeEventListener('jfc-chat:group-change', handleGroupChange);
        };
    }, [isLogged, updateGroupFilter]);

    // ============================================
    // Membership Check
    // ============================================

    const checkMembership = useCallback(async (groupID: string): Promise<boolean> => {
        if (!imsdk || !isLogged || !groupID) {
            return false;
        }

        // Simple logic: fetch conversations and check if we have rooms
        // This is more reliable than isJoinGroup API
        const fetchedRooms = await fetchConversations(imsdk);
        const hasRooms = fetchedRooms && fetchedRooms.length > 0;
        setIsActiveMember(hasRooms);
        return hasRooms;
    }, [imsdk, isLogged, fetchConversations]);

    const clearReconnectTimer = useCallback(() => {
        if (reconnectTimerRef.current) {
            clearTimeout(reconnectTimerRef.current);
            reconnectTimerRef.current = null;
        }
    }, []);

    const performConnectionRecovery = useCallback(async (reason: string, forceTokenRefresh: boolean = false): Promise<boolean> => {
        if (recoveryInProgressRef.current) return false;
        if (isBrowserOffline()) {
            clearReconnectTimer();
            setConnectionStatus('Connection Failed');
            return false;
        }

        const sdk = sdkRef.current || imsdk;
        if (!sdk || !currentUserID || !currentEmail || !jfcToken) {
            return false;
        }

        recoveryInProgressRef.current = true;
        try {
            setConnectionStatus('Connecting...');

            const openIMPlatformID = notificationService.getOpenIMPlatformID();
            const shouldRefreshToken =
                forceTokenRefresh ||
                !latestOpenIMTokenRef.current ||
                latestOpenIMPlatformIDRef.current !== openIMPlatformID;
            const token = shouldRefreshToken
                ? await getUserToken(jfcToken, currentEmail, openIMPlatformID)
                : latestOpenIMTokenRef.current;

            if (!token) {
                setConnectionStatus('Connection Failed');
                return false;
            }

            latestOpenIMTokenRef.current = token;
            latestOpenIMPlatformIDRef.current = openIMPlatformID;

            const loginConfig = {
                userID: currentUserID,
                token,
                platformID: openIMPlatformID,
                apiAddr: OpenIMConfig.apiAddr,
                wsAddr: OpenIMConfig.wsAddr
            };

            const operationID = `${Date.now()}_${reason}`;
            try {
                await loginToSDK(sdk, loginConfig, operationID);
            } catch (e: any) {
                // 10102: already logged in; treat as successful recovery
                if (e?.errCode !== 10102) throw e;
            }

            setIsLogged(true);
            setConnectionStatus('Connected');
            reconnectAttemptRef.current = 0;
            clearReconnectTimer();

            try {
                const fetchedRooms = await fetchConversations(sdk);
                const hasRooms = fetchedRooms.length > 0;
                setIsActiveMember(hasRooms);
                window.dispatchEvent(new CustomEvent('jfc-chat:on-member-status', {
                    detail: {
                        isMember: hasRooms,
                        groupID: currentGroupIDRef.current,
                        reason: 'connection_recovery',
                        timestamp: Date.now()
                    },
                    bubbles: true,
                    composed: true
                }));
            } catch (e) {
                console.debug('fetchConversations during recovery failed (non-critical):', e);
            }

            return true;
        } catch (error) {
            console.warn('Connection recovery failed:', error);
            setConnectionStatus('Connection Failed');
            return false;
        } finally {
            recoveryInProgressRef.current = false;
        }
    }, [imsdk, currentUserID, currentEmail, jfcToken, fetchConversations, clearReconnectTimer]);

    const queueReconnectAttempt = useCallback((reason: string, forceTokenRefresh: boolean = false) => {
        const MAX_RECOVERY_ATTEMPTS = 6;

        if (isBrowserOffline()) {
            clearReconnectTimer();
            return;
        }
        if (reconnectAttemptRef.current >= MAX_RECOVERY_ATTEMPTS) return;
        if (reconnectTimerRef.current || recoveryInProgressRef.current) return;
        if (!sdkRef.current && !imsdk) return;
        if (!currentUserID || !currentEmail || !jfcToken) return;

        const currentAttempt = reconnectAttemptRef.current;
        const delayMs = currentAttempt === 0
            ? 0
            : Math.min(30000, 1000 * Math.pow(2, currentAttempt - 1));

        reconnectTimerRef.current = setTimeout(async () => {
            reconnectTimerRef.current = null;
            reconnectAttemptRef.current += 1;
            await performConnectionRecovery(reason, forceTokenRefresh);
        }, delayMs);
    }, [imsdk, currentUserID, currentEmail, jfcToken, performConnectionRecovery, clearReconnectTimer]);

    // Trigger recovery when connection drops or token expires.
    useEffect(() => {
        if (connectionStatus === 'Token Expired') {
            queueReconnectAttempt('token_expired', true);
            return;
        }

        if (connectionStatus === 'Connection Failed') {
            queueReconnectAttempt('connect_failed', false);
            return;
        }

        if (connectionStatus === 'Connected') {
            reconnectAttemptRef.current = 0;
            clearReconnectTimer();
        }
    }, [connectionStatus, queueReconnectAttempt, clearReconnectTimer]);

    // Recover quickly when network returns; reflect offline state immediately.
    useEffect(() => {
        if (typeof window === 'undefined') return;

        const handleOnline = () => {
            reconnectAttemptRef.current = 0;
            clearReconnectTimer();
            queueReconnectAttempt('network_online', false);
        };

        const handleOffline = () => {
            clearReconnectTimer();
            setConnectionStatus('Connection Failed');
        };

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);
        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, [queueReconnectAttempt, clearReconnectTimer]);

    // Proactively refresh OpenIM token so recovery has a fresh token ready.
    useEffect(() => {
        if (!isLogged || !jfcToken || !currentEmail) return;

        const refreshToken = async () => {
            const openIMPlatformID = notificationService.getOpenIMPlatformID();
            const token = await getUserToken(jfcToken, currentEmail, openIMPlatformID);
            if (!token) return;

            const tokenChanged =
                token !== latestOpenIMTokenRef.current ||
                latestOpenIMPlatformIDRef.current !== openIMPlatformID;
            latestOpenIMTokenRef.current = token;
            latestOpenIMPlatformIDRef.current = openIMPlatformID;

            const sdk = sdkRef.current || imsdk;
            if (tokenChanged && sdk && typeof sdk.updateUserToken === 'function') {
                try {
                    await sdk.updateUserToken(token, Date.now().toString());
                } catch (e) {
                    console.debug('updateUserToken failed (non-critical):', e);
                }
            }
        };

        refreshToken().catch(() => {});
        tokenRefreshIntervalRef.current = setInterval(() => {
            refreshToken().catch(() => {});
        }, 25 * 60 * 1000);

        return () => {
            if (tokenRefreshIntervalRef.current) {
                clearInterval(tokenRefreshIntervalRef.current);
                tokenRefreshIntervalRef.current = null;
            }
        };
    }, [isLogged, jfcToken, currentEmail, imsdk]);

    useEffect(() => {
        return () => {
            clearReconnectTimer();
            clearAllTypingFailsafeTimers();
            if (tokenRefreshIntervalRef.current) {
                clearInterval(tokenRefreshIntervalRef.current);
                tokenRefreshIntervalRef.current = null;
            }
        };
    }, [clearAllTypingFailsafeTimers, clearReconnectTimer]);

    // ============================================
    // Initialize SDK & Login
    // ============================================

    // Note: userID parameter kept for backwards compatibility but not used - OpenIM uses email as userID
    const init = useCallback(async (_userID: string, jfcToken: string, userInfo?: Partial<UserDto>, groupID?: string, groupName?: string, customerNo?: string): Promise<boolean> => {
        // Increment generation to invalidate any in-progress init
        const currentGeneration = ++initGenerationRef.current;

        if (isHostChatDisabled()) {
            return false;
        }

        // If another init is in progress, allow it to be superseded
        // The stale init will check generation and bail out
        if (initializingRef.current) {
            // Wait briefly for the previous init to notice it's been superseded
            await new Promise(resolve => setTimeout(resolve, 100));
        }

        // Check if we've been superseded by an even newer init
        if (currentGeneration !== initGenerationRef.current || isHostChatDisabled()) {
            return false;
        }

        try {
            initializingRef.current = true;

            // Clear visible state immediately so old account data isn't shown
            roomsRef.current = [];
            selectedRoomRef.current = null;
            setRooms([]);
            setSelectedRoom(null);
            setMessages([]);
            setConversations([]);
            setIsActiveMember(false);

            // Ensure previous session is cleaned up
            if (sdkRef.current) {
                await logout();
                // Brief pause to ensure cleanup is processed
                await new Promise(resolve => setTimeout(resolve, 500));
            }

            // Bail out if a newer init has started
            if (currentGeneration !== initGenerationRef.current || isHostChatDisabled()) {
                initializingRef.current = false;
                return false;
            }

            setConnectionStatus('Connecting...');

            const uType = userInfo?.type || UserType.Customer;
            const userEmail = userInfo?.email || '';

            // IMPORTANT: OpenIM userID is the hex-encoded email
            const openimUserID = userEmail.split('').map(c => c.charCodeAt(0).toString(16)).join('');
            currentUserIDRef.current = openimUserID;
            setCurrentUserID(openimUserID);
            setCurrentEmail(userEmail);
            setJfcToken(jfcToken);

            // Check user role - only show chat for customers, not salesmen
            const roleResult = await getUserRole(jfcToken, userEmail);
            if (isHostChatDisabled()) {
                initializingRef.current = false;
                return false;
            }
            if (roleResult?.isSalesman) {
                setConnectionStatus('Disconnected');
                initializingRef.current = false;
                return false;
            }

            setUserType(uType);

            // Get OpenIM token using JFC token
            const openIMPlatformID = notificationService.getOpenIMPlatformID();
            const token = await getUserToken(jfcToken, userEmail, openIMPlatformID);
            if (isHostChatDisabled()) {
                initializingRef.current = false;
                return false;
            }

            if (!token) {
                console.error('Failed to get user token');
                setConnectionStatus('Connection Failed');
                initializingRef.current = false;
                return false;
            }
            latestOpenIMTokenRef.current = token;
            latestOpenIMPlatformIDRef.current = openIMPlatformID;

            // Get or create SDK singleton (prevents multiple initialization warnings)
            const sdk = await getOrCreateSDK();

            sdkRef.current = sdk;
            setImsdk(sdk);

            setupEventListeners(sdk);

            // Login - openimUserID was already computed above (hex-encoded email)
            const loginConfig = {
                userID: openimUserID,
                token,
                platformID: openIMPlatformID,
                apiAddr: OpenIMConfig.apiAddr,
                wsAddr: OpenIMConfig.wsAddr
            };

            const operationID = Date.now().toString();

            // Login using the singleton-aware login wrapper (prevents concurrent login attempts)
            let loginSuccess = false;
            for (let i = 0; i < 3; i++) {
                if (isHostChatDisabled()) {
                    initializingRef.current = false;
                    return false;
                }

                try {
                    await loginToSDK(sdk, loginConfig, operationID);
                    loginSuccess = true;
                    break;
                } catch (e: any) {
                    if (e?.errCode === 10102) {
                        const loggedInUserID = await getSDKLoginUserID(sdk, `${operationID}_outer_${i}`);
                        if (loggedInUserID === openimUserID || (!loggedInUserID && lastLoginUserID === openimUserID)) {
                            setIsLogged(true);
                            setConnectionStatus('Connected');
                            loginSuccess = true;
                            break;
                        }
                        await logoutSDKSession(sdk, `${operationID}_outer_logout_${i}`);
                    }
                    if (i < 2) {
                        // Wait 1s before retry
                        await new Promise(r => setTimeout(r, 1000));
                    }
                }
            }

            if (!loginSuccess) {
                throw new Error('Login failed after 3 attempts');
            }

            if (isHostChatDisabled()) {
                await logoutSDKSession(sdk, `${operationID}_disabled_logout`);
                initializingRef.current = false;
                return false;
            }

            // Store current account's groupID (customerId) for filtering OpenIM conversations
            console.log('[ChatContext] init: setting groupID(customerId) filter to', groupID, 'customerNo:', customerNo);
            setCurrentGroupID(groupID || null);
            currentGroupIDRef.current = groupID || null;
            setCurrentGroupName(groupName || null);
            currentGroupNameRef.current = groupName || null;
            currentCustomerNoRef.current = customerNo;

            // Initialize push notifications after successful login.
            // Pass the OpenIM user token so NotificationService can call
            // POST /third/fcm_update_token directly on the OpenIM server.
            notificationService.initialize(openimUserID, sdk, token).catch(err => {
                console.warn('Push notification setup failed (non-critical):', err);
            });

            const reconcileRooms = async (): Promise<boolean> => {
                if (currentGeneration !== initGenerationRef.current || isHostChatDisabled()) {
                    return false;
                }

                const fetchedRooms = await fetchConversations(sdk);
                if (currentGeneration !== initGenerationRef.current || isHostChatDisabled()) {
                    return false;
                }

                let hasRooms = fetchedRooms && fetchedRooms.length > 0;

                // If no conversation is available yet, verify/create the known group
                // in the background. This keeps startup fast while still enriching
                // the lightweight room once the SDK has caught up.
                if (!hasRooms && groupID) {
                    const syntheticRoom = await createSyntheticRoom(sdk, groupID, groupName, customerNo);
                    if (currentGeneration !== initGenerationRef.current || isHostChatDisabled()) {
                        return false;
                    }
                    if (syntheticRoom) {
                        hasRooms = true;
                        activateRoom(syntheticRoom);
                    }
                }

                setIsActiveMember(hasRooms || (!!groupID && selectedRoomRef.current?.groupID === groupID));
                return hasRooms;
            };

            if (groupID) {
                activateRoom(createFastRoom(groupID, groupName, customerNo));
                initializingRef.current = false;
                reconcileRooms().catch(error => {
                    console.debug('Background room reconciliation failed:', error);
                });
                return true;
            }

            await reconcileRooms();

            initializingRef.current = false;
            return true;
        } catch (error) {
            setConnectionStatus('Connection Failed');
            initializingRef.current = false;
            return false;
        }
    }, [setupEventListeners, fetchConversations, createSyntheticRoom, createFastRoom, activateRoom, logout]);

    // ============================================
    // Logout
    // ============================================



    // ============================================
    // Select Room
    // ============================================

    const selectRoom = useCallback((room: RoomDto | null) => {
        selectedRoomRef.current = room;
        setSelectedRoom(room);
        setMessages([]);
    }, []);

    // ============================================
    // Load Messages
    // ============================================

    const loadMessages = useCallback(async (conversationID: string, lastMsgID?: string, count: number = 40): Promise<MessageDto[]> => {
        if (!imsdk) return [];

        try {
            const operationID = Date.now().toString();
            const result = await imsdk.getAdvancedHistoryMessageList({
                conversationID,
                startClientMsgID: lastMsgID || '',
                count,
            }, operationID);

            if (!result || !result.data) {
                throw new Error('Invalid response from getAdvancedHistoryMessageList');
            }

            const { data } = result;

            let list = data.messageList || [];
            if (typeof list === 'string') list = JSON.parse(list);

            // Extract reactions from custom messages and build reactions map
            const reactionsFromHistory: MessageReactions = {};
            list.forEach((msg: MessageDto) => {
                if (msg.contentType === MessageType.CustomMessage && msg.customElem?.data) {
                    try {
                        const customData = JSON.parse(msg.customElem.data);
                        if (customData.type === 'reaction' && customData.emoji && customData.messageId && customData.userId) {
                            const { emoji, messageId, userId } = customData;
                            if (!reactionsFromHistory[messageId]) {
                                reactionsFromHistory[messageId] = {};
                            }
                            if (!reactionsFromHistory[messageId][emoji]) {
                                reactionsFromHistory[messageId][emoji] = [];
                            }
                            if (!reactionsFromHistory[messageId][emoji].includes(userId)) {
                                reactionsFromHistory[messageId][emoji].push(userId);
                            }
                        }
                    } catch (e) {
                        // Ignore parse errors for non-reaction custom messages
                    }
                }
            });

            // Update messageReactions state with reactions from history
            if (Object.keys(reactionsFromHistory).length > 0) {
                setMessageReactions(prev => {
                    const merged = { ...prev };
                    for (const [messageId, emojis] of Object.entries(reactionsFromHistory)) {
                        if (!merged[messageId]) {
                            merged[messageId] = {};
                        }
                        for (const [emoji, userIds] of Object.entries(emojis)) {
                            if (!merged[messageId][emoji]) {
                                merged[messageId][emoji] = [];
                            }
                            // Add unique userIds
                            for (const userId of userIds) {
                                if (!merged[messageId][emoji].includes(userId)) {
                                    merged[messageId][emoji].push(userId);
                                }
                            }
                        }
                    }
                    return merged;
                });
            }

            // 1. Extract read receipts from history
            interface ReadReceipt {
                senderId: string;
                timestamp: number;
                messageIds?: string[];
            }
            const historicalReceipts: ReadReceipt[] = [];

            // Filter out system messages and read receipts (we don't display receipts)
            const systemMsgTypes: number[] = [
                MessageType.GroupCreatedNotification,
                MessageType.GroupMemberMutedNotification,
                MessageType.MemberEnterNotification,
                MessageType.MemberInvitedNotification,
            ];

            list = list.filter((m: MessageDto) => {
                // Filter out system messages
                if (systemMsgTypes.includes(m.contentType)) return false;

                // Handle Custom Messages (Reactions & Read Receipts)
                if (m.contentType === MessageType.CustomMessage && m.customElem) {
                    // Filter out AND collect read receipts
                    const receipt = extractReadReceiptFromCustomMessage(m);
                    if (receipt) {
                        historicalReceipts.push(receipt);
                        return false; // Don't show receipt messages
                    }

                }

                return true;
            });

            // 2. Strip broad SDK isRead from outgoing messages unless SDK group
            // read metadata proves that a sales rep read the message.
            list = list.map((m: MessageDto) => {
                const salesReadAt = getSalesReadTimestampFromSdkMessage(
                    m,
                    currentUserIDRef.current,
                    selectedRoomRef.current?.members
                );
                if (salesReadAt) {
                    return { ...m, isRead: true, readAt: m.readAt || salesReadAt };
                }

                if (m.sendID === currentUserIDRef.current && m.isRead && !m.readAt) {
                    return { ...m, isRead: false };
                }
                return m;
            });

            // 3. Combine with pending receipts to catch ALL read statuses
            const allReceipts = [...historicalReceipts, ...pendingReadReceiptsRef.current];
            const receiptCacheGroupID =
                selectedRoomRef.current?.conversationID === conversationID
                    ? selectedRoomRef.current?.groupID
                    : roomsRef.current.find(room => room.conversationID === conversationID)?.groupID;

            allReceipts.forEach(receipt => {
                if (
                    shouldApplySalesReadReceipt(
                        receipt.senderId,
                        currentUserIDRef.current,
                        selectedRoomRef.current?.members
                    )
                ) {
                    rememberSalesReadReceipts(
                        currentUserIDRef.current,
                        receiptCacheGroupID,
                        receipt.messageIds || [],
                        receipt.timestamp
                    );
                }
            });

            // 4. Apply all receipts to the loaded messages
            const messagesWithReadStatus = list.map((message: MessageDto) => {
                const cachedReadAt = getCachedSalesReadAt(
                    message,
                    currentUserIDRef.current,
                    receiptCacheGroupID
                );
                if (cachedReadAt && (!message.isRead || !message.readAt)) {
                    return { ...message, isRead: true, readAt: cachedReadAt };
                }

                // Check if this message should be marked as read based on ANY receipt (historical or pending)
                for (const receipt of allReceipts) {
                    // Only mark as read if a sales rep read our messages (not another customer)
                    const isSalesReadingOurMessages = shouldApplySalesReadReceipt(
                        receipt.senderId,
                        currentUserIDRef.current,
                        selectedRoomRef.current?.members
                    );

                    if (isSalesReadingOurMessages) {
                        if (
                            receiptAppliesToMessage(receipt, message, currentUserIDRef.current) &&
                            (!message.isRead || !message.readAt)
                        ) {
                            return { ...message, isRead: true, readAt: receipt.timestamp };
                        }
                    }
                    // Ignore read receipts from other customers - only sales rep reads matter
                }
                return message;
            });

            // Clear applied receipts that are older than the oldest message we just loaded
            // (Only clear from pendingReadReceiptsRef, historical ones are local to this function scope)
            if (messagesWithReadStatus.length > 0) {
                const oldestMessageTime = Math.min(...messagesWithReadStatus.map((m: MessageDto) => m.sendTime));
                setPendingReadReceipts(prev => {
                    const filtered = prev.filter(receipt => receipt.timestamp >= oldestMessageTime);
                    pendingReadReceiptsRef.current = filtered;
                    return filtered;
                });
            }

            // Upgrade http:// URLs to https://, apply read status, and collapse
            // duplicate rows that can appear after native resume/offline replay.
            const finalMessages = dedupeMessagesByIdentity(
                messagesWithReadStatus.map(upgradeMessageUrls)
            );

            // CRITICAL: Update state with loaded messages so that read status updates can work
            setMessages(finalMessages);

            return finalMessages;
        } catch (err) {
            console.error('Failed to load messages:', err);
            console.error('Error details:', {
                conversationID,
                lastMsgID,
                count,
                errorMessage: err instanceof Error ? err.message : 'Unknown error',
                errorStack: err instanceof Error ? err.stack : undefined,
                errorType: typeof err,
                error: err
            });
            return [];
        }
    }, [imsdk, isLogged]);


    // Helper to check and open conversation if status is 'closing'
    const checkAndOpenConversation = useCallback(async (groupID: string) => {
        if (!groupID || !imsdk) return;

        // Find the conversation
        const conversation = conversations.find(c => c.groupID === groupID);
        if (!conversation) return;

        try {
            let exData: any = {};
            if (conversation.ex) {
                try {
                    exData = JSON.parse(conversation.ex);
                } catch (e) {
                    console.error('Error parsing conversation ex data:', e);
                }
            }

            // Check if status is 'closing'
            if (exData.status === 'closing') {
                console.log(`Auto-opening conversation ${conversation.conversationID} (was closing)`);

                // Update status to 'opening'
                exData.status = 'opening';
                const updatedEx = JSON.stringify(exData);

                // 1. Update SDK
                const operationID = Date.now().toString();
                // Note: setConversationReq equivalent in WASM SDK
                await imsdk.setConversation(conversation.conversationID, { ex: updatedEx }, operationID);

                // 2. Update local state optimistically
                setConversations(prev => prev.map(c => {
                    if (c.conversationID === conversation.conversationID) {
                        return { ...c, ex: updatedEx };
                    }
                    return c;
                }));

                // Also update rooms if needed
                setRooms(prev => prev.map(r => {
                    if (r.conversationID === conversation.conversationID) {
                        return {
                            ...r,
                            conversation: {
                                ...r.conversation!,
                                ex: updatedEx
                            }
                        };
                    }
                    return r;
                }));
            }
        } catch (e) {
            console.error('Error checking/opening conversation:', e);
        }
    }, [conversations, imsdk]);

    // ============================================
    // Send Messages
    // ============================================

    const ensureSDKSessionMatchesCurrentUser = useCallback(async (): Promise<boolean> => {
        const sdk = sdkRef.current || imsdk;
        const expectedUserID = currentUserIDRef.current;
        if (!sdk || !expectedUserID) return false;

        const activeUserID = await getSDKLoginUserID(sdk, `${Date.now()}_verify_send_user`);
        if (activeUserID && activeUserID !== expectedUserID) {
            console.error('OpenIM SDK account mismatch before send:', {
                activeUserID,
                expectedUserID,
            });
            await logoutSDKSession(sdk, `${Date.now()}_mismatch_logout`);
            setIsLogged(false);
            setConnectionStatus('Connection Failed');
            return false;
        }

        return true;
    }, [imsdk]);

    const getMessageIdentity = (message: unknown): { clientMsgID?: string; seq?: number } => {
        const msg = message as { clientMsgID?: unknown; seq?: unknown } | null | undefined;
        const clientMsgID = typeof msg?.clientMsgID === 'string' ? msg.clientMsgID : undefined;
        const seq = typeof msg?.seq === 'number' ? msg.seq : undefined;
        return { clientMsgID, seq };
    };

    const normalizeOfflinePushText = (value?: string | null): string | undefined => {
        const trimmed = value?.trim();
        return trimmed ? trimmed : undefined;
    };

    const buildOfflinePushSenderLabel = (customerName?: string, customerNo?: string): string | undefined => {
        if (!customerName && !customerNo) return undefined;
        return getRoomDisplayName(customerName || '', customerNo, 32);
    };

    const getOfflinePushCustomerMeta = (groupID: string): { customerName?: string; customerNo?: string; senderLabel?: string } => {
        const room = roomsRef.current.find(item => item.groupID === groupID);
        const customerName = room?.groupName?.trim() || currentGroupNameRef.current?.trim() || undefined;
        const customerNo = room?.customerNo?.trim() || currentCustomerNoRef.current?.trim() || undefined;

        return {
            customerName,
            customerNo,
            senderLabel: buildOfflinePushSenderLabel(customerName, customerNo),
        };
    };

    // Build offlinePushInfo with routing data for push notification deep-linking.
    // Include per-message identity so downstream push layers can avoid collapsing
    // multiple messages into one notification.
    const buildOfflinePushInfo = (groupID: string, title: string, desc: string, message?: unknown) => {
        const { clientMsgID, seq } = getMessageIdentity(message);
        const uniqueId = clientMsgID || (seq !== undefined ? `seq_${seq}` : `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);
        const { customerName, customerNo, senderLabel } = getOfflinePushCustomerMeta(groupID);
        // Terminated recipients only see what the backend sends, so prefer a concrete
        // sender/chat label over generic message-type titles such as "New message".
        const resolvedTitle =
            normalizeOfflinePushText(senderLabel) ||
            normalizeOfflinePushText(title) ||
            'New message';
        const resolvedDesc =
            normalizeOfflinePushText(desc) ||
            normalizeOfflinePushText(getLocalizedMessagePreviewText(message as MessageDto | null | undefined)) ||
            '';

        return {
            title: resolvedTitle,
            desc: resolvedDesc,
            iOSPushSound: 'default',
            iOSBadgeCount: true,
            operatorUserID: currentUserIDRef.current,
            ex: JSON.stringify({
                conversationID: `sg_${groupID}`,
                sessionType: 3,
                sourceID: groupID,
                sendID: currentUserIDRef.current,
                groupID: groupID,
                customerID: groupID,
                customerName,
                customerNo,
                senderLabel,
                clientMsgID,
                seq,
                notificationTag: `chat-${groupID}-${uniqueId}`,
                priority: 'high',
                apnsPriority: '10',
            }),
        };
    };

    const buildReadStatusOfflinePushInfo = (groupID: string, conversationID: string) => ({
        title: '',
        desc: '',
        iOSPushSound: '',
        iOSBadgeCount: false,
        ex: JSON.stringify({
            action: 'read_status',
            type: 'read_status',
            conversationID,
            sourceID: groupID,
            groupID,
            sendID: currentUserIDRef.current,
        }),
    });

    const sendTextMessage = useCallback(async (params: {
        text: string;
        groupID: string;
        requestId?: string;
        atUserIDList?: string[];
        atUsersInfo?: AtUsersInfoItem[];
    }): Promise<MessageDto | null> => {
        if (!imsdk || !isLogged) return null;
        if (!(await ensureSDKSessionMatchesCurrentUser())) return null;

        // Check and open conversation if needed
        checkAndOpenConversation(params.groupID);

        let pendingMessage: MessageDto | null = null;
        try {
            const operationID = Date.now().toString();
            const normalizedAtUserIDList = Array.from(
                new Set(
                    (params.atUserIDList || [])
                        .map(userID => (typeof userID === 'string' ? userID.trim() : ''))
                        .filter(Boolean)
                )
            );
            const hasMentions = normalizedAtUserIDList.length > 0;
            const atUsersInfoByID = new Map<string, string>();
            (params.atUsersInfo || []).forEach(item => {
                const atUserID = typeof item?.atUserID === 'string' ? item.atUserID.trim() : '';
                if (!atUserID) return;
                const groupNickname = typeof item?.groupNickname === 'string' ? item.groupNickname.trim() : '';
                atUsersInfoByID.set(atUserID, groupNickname || atUserID);
            });
            const normalizedAtUsersInfo: AtUsersInfoItem[] = normalizedAtUserIDList.map(atUserID => ({
                atUserID,
                groupNickname: atUsersInfoByID.get(atUserID) || atUserID,
            }));

            const response = hasMentions
                ? await imsdk.createTextAtMessage({
                    text: params.text,
                    atUserIDList: normalizedAtUserIDList,
                    atUsersInfo: normalizedAtUsersInfo,
                }, operationID)
                : await imsdk.createTextMessage(params.text, operationID);
            const createData = response?.data;

            if (response?.errCode && response.errCode !== 0) {
                console.error(
                    hasMentions ? 'SDK createTextAtMessage failed:' : 'SDK createTextMessage failed:',
                    response.errMsg
                );
                return null;
            }

            if (!createData) {
                console.error('SDK returned empty message data');
                return null;
            }

            let message: MessageDto = typeof createData === 'string' ? JSON.parse(createData) : createData;
            if (params.requestId) {
                message.ex = JSON.stringify({ requestId: params.requestId });
            }
            pendingMessage = message;

            const sendOptions = {
                recvID: '',
                groupID: params.groupID,
                message,
                offlinePushInfo: buildOfflinePushInfo(params.groupID, 'New message', params.text, message)
            };

            const sendResponse = await withTimeout(
                imsdk.sendMessage(sendOptions, operationID),
                MESSAGE_SEND_TIMEOUT_MS,
                t.messageSendFailed
            );
            const sentData = sendResponse?.data;

            if (sendResponse?.errCode && sendResponse.errCode !== 0) {
                console.error('SDK sendMessage failed:', sendResponse.errMsg);
                const failedMessage = markMessageAsFailed(message, sendResponse, t.messageSendFailed);
                setMessages(prev => upsertMessageByClientID(prev, failedMessage));
                return failedMessage;
            }

            const finalMessage = typeof sentData === 'string' ? JSON.parse(sentData) : sentData;

            // Add to message list
            setMessages(prev => upsertMessageByClientID(prev, finalMessage));

            return finalMessage;
        } catch (err) {
            console.error('Failed to send text message:', err);
            if (pendingMessage) {
                const failedMessage = markMessageAsFailed(pendingMessage, err, t.messageSendFailed);
                setMessages(prev => upsertMessageByClientID(prev, failedMessage));
                return failedMessage;
            }
            return null;
        }
    }, [imsdk, isLogged, ensureSDKSessionMatchesCurrentUser, t]);

    const sendImageMessage = useCallback(async (params: { file: File; groupID: string; requestId?: string; albumId?: string }): Promise<MessageDto | null> => {
        if (!imsdk || !isLogged) return null;
        if (!(await ensureSDKSessionMatchesCurrentUser())) return null;

        // Check and open conversation if needed
        checkAndOpenConversation(params.groupID);

        let pendingMessage: MessageDto | null = null;
        try {
            const operationID = Date.now().toString();

            // Create image message
            const createParams = {
                sourcePicture: {
                    uuid: createUniqueId(),
                    type: params.file.type,
                    size: params.file.size,
                    width: 0,
                    height: 0,
                    url: ''
                },
                bigPicture: {
                    uuid: createUniqueId(),
                    type: params.file.type,
                    size: params.file.size,
                    width: 0,
                    height: 0,
                    url: ''
                },
                snapshotPicture: {
                    uuid: createUniqueId(),
                    type: params.file.type,
                    size: params.file.size,
                    width: 0,
                    height: 0,
                    url: ''
                },
                sourcePath: params.file.name,
                file: params.file
            };

            const response = await imsdk.createImageMessageByFile(createParams, operationID);
            const createData = response?.data;

            if (response?.errCode && response.errCode !== 0) {
                console.error('SDK createImageMessageByFile failed:', response.errMsg);
                return null;
            }

            if (!createData) {
                console.error('SDK returned empty image message data');
                return null;
            }

            let message: MessageDto = typeof createData === 'string' ? JSON.parse(createData) : createData;

            // Add albumID and/or requestId to ex field
            const exData: any = {};
            if (params.requestId) {
                exData.requestId = params.requestId;
            }
            if (params.albumId) {
                exData.albumID = params.albumId;
            }
            if (Object.keys(exData).length > 0) {
                message.ex = JSON.stringify(exData);
            }
            pendingMessage = message;

            const sendOptions = {
                recvID: '',
                groupID: params.groupID,
                message,
                offlinePushInfo: buildOfflinePushInfo(params.groupID, 'New image', 'Image', message)
            };

            const sendResponse = await imsdk.sendMessage(sendOptions, operationID);
            const sentData = sendResponse?.data;

            if (sendResponse?.errCode && sendResponse.errCode !== 0) {
                console.error('SDK sendMessage failed for image:', sendResponse.errMsg);
                const failedMessage = markMessageAsFailed(message, sendResponse, t.photoUploadFailed);
                setMessages(prev => upsertMessageByClientID(prev, failedMessage));
                return failedMessage;
            }

            const finalMessage = typeof sentData === 'string' ? JSON.parse(sentData) : sentData;

            // Add to ChatContext's message list so read receipts can update it
            setMessages(prev => upsertMessageByClientID(prev, finalMessage));

            return finalMessage;
        } catch (err) {
            console.error('Failed to send image message:', {
                error: err,
                message: err instanceof Error ? err.message : String(err),
                stack: err instanceof Error ? err.stack : undefined,
                fileName: params.file.name,
                fileSize: params.file.size,
                albumId: params.albumId
            });
            if (pendingMessage) {
                const failedMessage = markMessageAsFailed(pendingMessage, err, t.photoUploadFailed);
                setMessages(prev => upsertMessageByClientID(prev, failedMessage));
                return failedMessage;
            }
            return null;
        }
    }, [imsdk, isLogged, ensureSDKSessionMatchesCurrentUser, t]);

    const sendVideoMessage = useCallback(async (params: { file: File; groupID: string; thumbnail: File; duration: number; requestId?: string }): Promise<MessageDto | null> => {
        if (!imsdk || !isLogged) return null;
        if (!(await ensureSDKSessionMatchesCurrentUser())) return null;

        // Check and open conversation if needed
        checkAndOpenConversation(params.groupID);

        let pendingMessage: MessageDto | null = null;
        try {
            const operationID = Date.now().toString();

            const createParams = {
                videoPath: params.file.name,
                videoType: params.file.type,
                duration: Math.round(params.duration),
                snapshotPath: params.thumbnail.name,
                videoUUID: createUniqueId(),
                videoUrl: '',
                videoSize: params.file.size,
                snapshotUUID: createUniqueId(),
                snapshotSize: params.thumbnail.size,
                snapshotUrl: '',
                snapshotWidth: 0,
                snapshotHeight: 0,
                snapShotType: params.thumbnail.type,
                videoFile: params.file,
                snapshotFile: params.thumbnail
            };

            let response;
            try {
                response = await imsdk.createVideoMessageByFile(createParams, operationID);
            } catch (err) {
                console.error('SDK threw error during createVideoMessageByFile:', err);
                return null;
            }

            const createData = response?.data;

            if (response?.errCode && response.errCode !== 0) {
                console.error('SDK createVideoMessageByFile failed:', response.errMsg);
                return null;
            }

            if (!createData) {
                console.error('SDK returned empty video message data');
                return null;
            }

            let message: MessageDto = typeof createData === 'string' ? JSON.parse(createData) : createData;

            if (params.requestId) {
                message.ex = JSON.stringify({ requestId: params.requestId });
            }
            pendingMessage = message;

            const sendOptions = {
                recvID: '',
                groupID: params.groupID,
                message,
                offlinePushInfo: buildOfflinePushInfo(params.groupID, 'New video', 'Video', message)
            };

            const sendResponse = await imsdk.sendMessage(sendOptions, operationID);
            const sentData = sendResponse?.data;

            if (sendResponse?.errCode && sendResponse.errCode !== 0) {
                console.error('SDK sendMessage failed for video:', sendResponse.errMsg);
                const failedMessage = markMessageAsFailed(message, sendResponse, t.attachmentUploadFailed);
                setMessages(prev => upsertMessageByClientID(prev, failedMessage));
                return failedMessage;
            }

            const finalMessage = typeof sentData === 'string' ? JSON.parse(sentData) : sentData;

            // Add to ChatContext's message list so read receipts can update it
            setMessages(prev => upsertMessageByClientID(prev, finalMessage));

            return finalMessage;
        } catch (err) {
            console.error('Failed to send video message:', err);
            if (pendingMessage) {
                const failedMessage = markMessageAsFailed(pendingMessage, err, t.attachmentUploadFailed);
                setMessages(prev => upsertMessageByClientID(prev, failedMessage));
                return failedMessage;
            }
            return null;
        }
    }, [imsdk, isLogged, ensureSDKSessionMatchesCurrentUser, t]);

    const sendFileMessage = useCallback(async (params: { file: File; groupID: string; requestId?: string }): Promise<MessageDto | null> => {
        if (!imsdk || !isLogged) return null;
        if (!(await ensureSDKSessionMatchesCurrentUser())) return null;

        // Check and open conversation if needed
        checkAndOpenConversation(params.groupID);

        let pendingMessage: MessageDto | null = null;
        try {
            const operationID = Date.now().toString();

            const createParams = {
                filePath: params.file.name,
                fileName: params.file.name,
                uuid: createUniqueId(),
                sourceUrl: '',
                fileSize: params.file.size,
                fileType: params.file.type,
                file: params.file
            };

            const response = await imsdk.createFileMessageByFile(createParams, operationID);
            const createData = response?.data;

            if (response?.errCode && response.errCode !== 0) {
                console.error('SDK createFileMessageByFile failed:', response.errMsg);
                return null;
            }

            if (!createData) {
                console.error('SDK returned empty file message data');
                return null;
            }

            let message: MessageDto = typeof createData === 'string' ? JSON.parse(createData) : createData;

            if (params.requestId) {
                message.ex = JSON.stringify({ requestId: params.requestId });
            }
            pendingMessage = message;

            const sendOptions = {
                recvID: '',
                groupID: params.groupID,
                message,
                offlinePushInfo: buildOfflinePushInfo(params.groupID, 'New file', params.file.name, message)
            };

            const sendResponse = await imsdk.sendMessage(sendOptions, operationID);
            const sentData = sendResponse?.data;

            if (sendResponse?.errCode && sendResponse.errCode !== 0) {
                console.error('SDK sendMessage failed for file:', sendResponse.errMsg);
                const failedMessage = markMessageAsFailed(message, sendResponse, t.attachmentUploadFailed);
                setMessages(prev => upsertMessageByClientID(prev, failedMessage));
                return failedMessage;
            }

            const finalMessage = typeof sentData === 'string' ? JSON.parse(sentData) : sentData;

            // Add to ChatContext's message list so read receipts can update it
            setMessages(prev => upsertMessageByClientID(prev, finalMessage));

            return finalMessage;
        } catch (err) {
            console.error('Failed to send file message:', err);
            if (pendingMessage) {
                const failedMessage = markMessageAsFailed(pendingMessage, err, t.attachmentUploadFailed);
                setMessages(prev => upsertMessageByClientID(prev, failedMessage));
                return failedMessage;
            }
            return null;
        }
    }, [imsdk, isLogged, ensureSDKSessionMatchesCurrentUser, t]);

    const sendSoundMessage = useCallback(async (params: { file: File; groupID: string; duration: number; requestId?: string }): Promise<MessageDto | null> => {
        if (!imsdk || !isLogged) return null;
        if (!(await ensureSDKSessionMatchesCurrentUser())) return null;

        // Check and open conversation if needed
        checkAndOpenConversation(params.groupID);

        let pendingMessage: MessageDto | null = null;
        try {
            const operationID = Date.now().toString();

            const createParams = {
                soundPath: params.file.name,
                uuid: createUniqueId(),
                sourceUrl: '',
                dataSize: params.file.size,
                duration: params.duration,
                soundType: params.file.type,
                file: params.file
            };

            const response = await imsdk.createSoundMessageByFile(createParams, operationID);
            const createData = response?.data;

            if (response?.errCode && response.errCode !== 0) {
                console.error('SDK createSoundMessageByFile failed:', response.errMsg);
                return null;
            }

            if (!createData) {
                console.error('SDK returned empty sound message data');
                return null;
            }

            let message: MessageDto = typeof createData === 'string' ? JSON.parse(createData) : createData;

            if (params.requestId) {
                message.ex = JSON.stringify({ requestId: params.requestId });
            }
            pendingMessage = message;

            const sendOptions = {
                recvID: '',
                groupID: params.groupID,
                message,
                offlinePushInfo: buildOfflinePushInfo(params.groupID, 'Voice message', 'Voice', message)
            };

            const sendResponse = await imsdk.sendMessage(sendOptions, operationID);
            const sentData = sendResponse?.data;

            if (sendResponse?.errCode && sendResponse.errCode !== 0) {
                console.error('SDK sendMessage failed for sound:', sendResponse.errMsg);
                const failedMessage = markMessageAsFailed(message, sendResponse, t.attachmentUploadFailed);
                setMessages(prev => upsertMessageByClientID(prev, failedMessage));
                return failedMessage;
            }

            const finalMessage = typeof sentData === 'string' ? JSON.parse(sentData) : sentData;

            // Add to ChatContext's message list so read receipts can update it
            setMessages(prev => upsertMessageByClientID(prev, finalMessage));

            return finalMessage;
        } catch (err) {
            console.error('Failed to send sound message:', err);
            if (pendingMessage) {
                const failedMessage = markMessageAsFailed(pendingMessage, err, t.attachmentUploadFailed);
                setMessages(prev => upsertMessageByClientID(prev, failedMessage));
                return failedMessage;
            }
            return null;
        }
    }, [imsdk, isLogged, ensureSDKSessionMatchesCurrentUser, t]);

    const sendLocationMessage = useCallback(async (params: { latitude: number; longitude: number; description: string; groupID: string; requestId?: string }): Promise<MessageDto | null> => {
        if (!imsdk || !isLogged) return null;
        if (!(await ensureSDKSessionMatchesCurrentUser())) return null;

        // Check and open conversation if needed
        checkAndOpenConversation(params.groupID);

        let pendingMessage: MessageDto | null = null;
        try {
            const operationID = Date.now().toString();

            const createParams = {
                description: params.description,
                longitude: params.longitude,
                latitude: params.latitude
            };

            const response = await withTimeout(
                imsdk.createLocationMessage(createParams, operationID),
                MESSAGE_SEND_TIMEOUT_MS,
                t.messageSendFailed
            );
            const createData = response?.data;

            if (response?.errCode && response.errCode !== 0) {
                console.error('SDK createLocationMessage failed:', response.errMsg);
                return null;
            }

            if (!createData) {
                console.error('SDK returned empty location message data');
                return null;
            }

            let message: MessageDto = typeof createData === 'string' ? JSON.parse(createData) : createData;

            if (params.requestId) {
                message.ex = JSON.stringify({ requestId: params.requestId });
            }
            pendingMessage = message;

            const sendOptions = {
                recvID: '',
                groupID: params.groupID,
                message,
                offlinePushInfo: buildOfflinePushInfo(params.groupID, 'Location shared', params.description, message)
            };

            const sendResponse = await withTimeout(
                imsdk.sendMessage(sendOptions, operationID),
                MESSAGE_SEND_TIMEOUT_MS,
                t.messageSendFailed
            );
            const sentData = sendResponse?.data;

            if (sendResponse?.errCode && sendResponse.errCode !== 0) {
                console.error('SDK sendMessage failed for location:', sendResponse.errMsg);
                const failedMessage = markMessageAsFailed(message, sendResponse, t.messageSendFailed);
                setMessages(prev => upsertMessageByClientID(prev, failedMessage));
                return failedMessage;
            }

            const finalMessage = typeof sentData === 'string' ? JSON.parse(sentData) : sentData;

            // Add to ChatContext's message list so read receipts can update it
            setMessages(prev => upsertMessageByClientID(prev, finalMessage));

            return finalMessage;
        } catch (err) {
            console.error('Failed to send location message:', err);
            if (pendingMessage) {
                const failedMessage = markMessageAsFailed(pendingMessage, err, t.messageSendFailed);
                setMessages(prev => upsertMessageByClientID(prev, failedMessage));
                return failedMessage;
            }
            return null;
        }
    }, [imsdk, isLogged, ensureSDKSessionMatchesCurrentUser, t]);

    const sendReplyMessage = useCallback(async (params: { text: string; replyItem: MessageDto; groupID: string; requestId?: string }): Promise<MessageDto | null> => {
        if (!imsdk || !isLogged) return null;
        if (!(await ensureSDKSessionMatchesCurrentUser())) return null;

        // Check and open conversation if needed
        checkAndOpenConversation(params.groupID);

        let pendingMessage: MessageDto | null = null;
        try {
            const operationID = Date.now().toString();

            // Use createAdvancedQuoteMessage which accepts MessageItem object directly
            // QuoteMsgParams: { text: string; message: string }  <- JSON string
            // AdvancedQuoteMsgParams: { text: string; message: MessageItem } <- object

            // Parse attachedInfo if it's a string, or use empty object with required structure
            let attachedInfoElem = {
                groupHasReadInfo: {
                    hasReadCount: 0,
                    unreadCount: 0,
                    hasReadUserIDList: [],
                    groupMemberCount: 0
                },
                isPrivateChat: false,
                isEncryption: false,
                inEncryptStatus: false,
                burnDuration: 0,
                hasReadTime: 0
            };

            if (params.replyItem.attachedInfo) {
                try {
                    const parsed = typeof params.replyItem.attachedInfo === 'string'
                        ? JSON.parse(params.replyItem.attachedInfo)
                        : params.replyItem.attachedInfo;
                    attachedInfoElem = { ...attachedInfoElem, ...parsed };
                } catch (e) {
                    console.warn('Failed to parse attachedInfo:', e);
                }
            }

            // SDK expects MessageItem with specific required fields
            // Ensure all required fields are present with proper types
            const messageWithRequired = {
                clientMsgID: params.replyItem.clientMsgID,
                serverMsgID: params.replyItem.serverMsgID || '',
                createTime: params.replyItem.createTime,
                sendTime: params.replyItem.sendTime,
                sessionType: params.replyItem.sessionType || 3, // 3 = group session
                sendID: params.replyItem.sendID,
                recvID: params.replyItem.recvID || '',
                msgFrom: params.replyItem.msgFrom || 100, // 100 = user message
                contentType: params.replyItem.contentType,
                senderPlatformID: params.replyItem.platformID || 5, // 5 = web
                senderNickname: params.replyItem.senderNickname || '',
                senderFaceUrl: params.replyItem.senderFaceUrl || '',
                groupID: params.replyItem.groupID || params.groupID,
                content: params.replyItem.content || '',
                seq: params.replyItem.seq || 0,
                isRead: params.replyItem.isRead || false,
                status: params.replyItem.status || 2, // 2 = success
                ex: params.replyItem.ex || '',
                localEx: params.replyItem.localEx || '',
                attachedInfoElem,
                // Include element types if present
                ...(params.replyItem.textElem && { textElem: params.replyItem.textElem }),
                ...(params.replyItem.pictureElem && { pictureElem: params.replyItem.pictureElem }),
                ...(params.replyItem.videoElem && { videoElem: params.replyItem.videoElem }),
                ...(params.replyItem.fileElem && { fileElem: params.replyItem.fileElem }),
                ...(params.replyItem.soundElem && { soundElem: params.replyItem.soundElem }),
                ...(params.replyItem.locationElem && { locationElem: params.replyItem.locationElem }),
                ...(params.replyItem.quoteElem && { quoteElem: params.replyItem.quoteElem }),
                ...(params.replyItem.customElem && { customElem: params.replyItem.customElem }),
            } as any;

            const response = await withTimeout(
                imsdk.createAdvancedQuoteMessage(
                    {
                        text: params.text,
                        message: messageWithRequired
                    },
                    operationID
                ),
                MESSAGE_SEND_TIMEOUT_MS,
                t.messageSendFailed
            );

            // Handle different response formats
            let createData;

            // Check if response has data property
            if (response?.data) {
                createData = response.data;
            }
            // Check if response is an array (SDK sometimes returns array format)
            else if (Array.isArray(response) && response.length > 0) {
                createData = response[0];
            }
            // If response itself is a string, use it directly
            else if (typeof response === 'string') {
                createData = response;
            }

            if (response?.errCode && response.errCode !== 0) {
                console.error('SDK createQuoteMessage failed:', response.errMsg);
                return null;
            }

            if (!createData || createData === '') {
                console.warn('SDK returned empty quote message data from createAdvancedQuoteMessage. Retrying with createQuoteMessage.');

                try {
                    // Fallback to simpler createQuoteMessage
                    // Note: QuoteMsgParams expects message as JSON string, not object
                    const quoteResponse = await withTimeout(
                        imsdk.createQuoteMessage(
                            {
                                text: params.text,
                                message: JSON.stringify(messageWithRequired)
                            },
                            operationID
                        ),
                        MESSAGE_SEND_TIMEOUT_MS,
                        t.messageSendFailed
                    );

                    if (quoteResponse?.data) {
                        createData = quoteResponse.data;
                    } else if (Array.isArray(quoteResponse) && quoteResponse.length > 0) {
                        createData = quoteResponse[0];
                    } else if (typeof quoteResponse === 'string') {
                        createData = quoteResponse;
                    }
                } catch (fallbackErr) {
                    console.error('Fallback createQuoteMessage failed:', fallbackErr);
                }

                // If still empty after fallback
                if (!createData || createData === '') {
                    console.error('SDK returned empty quote message data');
                    return null;
                }
            }

            let message: MessageDto = typeof createData === 'string' ? JSON.parse(createData) : createData;

            if (params.requestId) {
                message.ex = JSON.stringify({ requestId: params.requestId });
            }
            pendingMessage = message;

            const sendOptions = {
                recvID: '',
                groupID: params.groupID,
                message,
                offlinePushInfo: buildOfflinePushInfo(params.groupID, 'Reply', params.text, message)
            };

            const sendResponse = await withTimeout(
                imsdk.sendMessage(sendOptions, operationID),
                MESSAGE_SEND_TIMEOUT_MS,
                t.messageSendFailed
            );
            const sentData = sendResponse?.data;

            if (sendResponse?.errCode && sendResponse.errCode !== 0) {
                console.error('SDK sendMessage failed for reply:', sendResponse.errMsg);
                const failedMessage = markMessageAsFailed(message, sendResponse, t.messageSendFailed);
                setMessages(prev => upsertMessageByClientID(prev, failedMessage));
                return failedMessage;
            }

            const finalMessage = typeof sentData === 'string' ? JSON.parse(sentData) : sentData;

            // Add to ChatContext's message list so read receipts can update it
            setMessages(prev => upsertMessageByClientID(prev, finalMessage));

            return finalMessage;
        } catch (err) {
            console.error('Failed to send reply message:', err);
            if (pendingMessage) {
                const failedMessage = markMessageAsFailed(pendingMessage, err, t.messageSendFailed);
                setMessages(prev => upsertMessageByClientID(prev, failedMessage));
                return failedMessage;
            }
            return null;
        }
    }, [imsdk, isLogged, ensureSDKSessionMatchesCurrentUser, t]);

    const sendAlbumMessage = useCallback(async (params: { files: File[]; groupID: string; onProgress?: (current: number, total: number) => void }): Promise<MessageDto[] | null> => {
        if (!imsdk || !isLogged || params.files.length === 0) {
            return null;
        }
        if (!(await ensureSDKSessionMatchesCurrentUser())) return null;

        if (!params.groupID || params.groupID === '') {
            console.error('Cannot send album: groupID is empty');
            return null;
        }

        // Check and open conversation if needed
        checkAndOpenConversation(params.groupID);

        try {
            // Generate unique album ID
            const albumId = `album_${Date.now()}_${params.files.length}`;

            // Send images individually with albumID - no synthetic album message
            const sentMessages: MessageDto[] = [];
            const clientMsgIDs: string[] = [];

            for (let i = 0; i < params.files.length; i++) {
                const imageFile = params.files[i];

                // Update progress
                if (params.onProgress) {
                    params.onProgress(i + 1, params.files.length);
                }

                try {
                    const sentMessage = await sendImageMessage({
                        file: imageFile,
                        groupID: params.groupID,
                        albumId: albumId
                    });

                    if (sentMessage) {
                        sentMessages.push(sentMessage);
                        if (sentMessage.status !== MessageStatus.Failed && sentMessage.clientMsgID) {
                            clientMsgIDs.push(sentMessage.clientMsgID);
                        }
                    }
                } catch (err) {
                    console.error('Error sending album image:', err);
                }
            }

            // Cache album information
            if (clientMsgIDs.length > 0) {
                const { cacheAlbumInfo } = await import('../services/AlbumCacheService');
                cacheAlbumInfo(albumId, clientMsgIDs);
            }

            if (sentMessages.length > 0) {
                // Add or update sent messages in state with complete URL data
                // This ensures the album is displayed immediately after sending with correct URLs
                setMessages(prev => {
                    return sentMessages.reduce(
                        (next, message) => upsertMessageByClientID(next, message),
                        prev
                    );
                });

                // Return all sent messages with complete URL data
                return sentMessages;
            } else {
                return null;
            }
        } catch (err) {
            console.error('Failed to send album message:', err);
            return null;
        }
    }, [imsdk, isLogged, sendImageMessage, ensureSDKSessionMatchesCurrentUser]);

    const revokeMessage = useCallback(async (params: { clientMsgID: string; conversationID: string }): Promise<boolean> => {
        if (!imsdk || !isLogged) return false;

        try {
            const operationID = Date.now().toString();
            await imsdk.revokeMessage(params, operationID);
            return true;
        } catch (err) {
            console.error('Failed to revoke message:', err);
            return false;
        }
    }, [imsdk, isLogged]);

    const deleteMessageForMe = useCallback(async (params: { clientMsgID: string; conversationID: string }): Promise<boolean> => {
        if (!imsdk || !isLogged) return false;

        try {
            const operationID = Date.now().toString();
            // Delete message from local storage only (not from server for others)
            await imsdk.deleteMessageFromLocalStorage(params, operationID);
            return true;
        } catch (err) {
            console.error('Failed to delete message locally:', err);
            return false;
        }
    }, [imsdk, isLogged]);

    // ============================================
    // Send Reaction
    // ============================================

    const sendReaction = useCallback(async (params: { emoji: string; messageId: string; groupID: string }): Promise<boolean> => {
        if (!imsdk || !isLogged) return false;
        if (!(await ensureSDKSessionMatchesCurrentUser())) return false;

        const { emoji, messageId, groupID } = params;

        // 1. Optimistic Update: Add to local state immediately for instant UI feedback
        setMessageReactions(prev => {
            const newReactions = { ...prev };

            if (!newReactions[messageId]) {
                newReactions[messageId] = {};
            }

            if (!newReactions[messageId][emoji]) {
                newReactions[messageId][emoji] = [];
            }

            if (!newReactions[messageId][emoji].includes(currentUserID)) {
                newReactions[messageId][emoji] = [...newReactions[messageId][emoji], currentUserID];
            }

            return newReactions;
        });

        try {
            const operationID = Date.now().toString();

            // 2. Prepare Data (matching Flutter format)
            const reactionData = {
                type: 'reaction',
                emoji,
                messageId,
                userId: currentUserID
            };

            // 3. Create Custom Message
            const response = await imsdk.createCustomMessage(
                {
                    data: JSON.stringify(reactionData),
                    extension: 'reaction',
                    description: t.reactionNotificationDesc
                },
                operationID
            );

            if (response?.errCode && response.errCode !== 0) {
                console.error('SDK createCustomMessage failed:', response.errMsg);
                throw new Error(response.errMsg);
            }

            const createData = response?.data;
            if (!createData) {
                throw new Error('SDK returned empty custom message data');
            }

            const message = typeof createData === 'string' ? JSON.parse(createData) : createData;

            // 4. Send Message
            const sendOptions = {
                recvID: '',
                groupID,
                message,
                isOnlineOnly: false,
                offlinePushInfo: buildOfflinePushInfo(groupID, t.reactionNotificationTitle, t.reactionNotificationDesc, message)
            };

            const sendResponse = await imsdk.sendMessage(sendOptions, operationID);

            if (sendResponse?.errCode && sendResponse.errCode !== 0) {
                console.error('SDK sendMessage failed for reaction:', sendResponse.errMsg);
                throw new Error(sendResponse.errMsg);
            }

            return true;
        } catch (err) {
            console.error('Error sending reaction:', err);

            // 5. Rollback: Remove optimistic reaction on failure
            setMessageReactions(prev => {
                const newReactions = { ...prev };

                if (newReactions[messageId]?.[emoji]?.includes(currentUserID)) {
                    newReactions[messageId][emoji] = newReactions[messageId][emoji].filter(
                        id => id !== currentUserID
                    );

                    // Cleanup empty arrays
                    if (newReactions[messageId][emoji].length === 0) {
                        delete newReactions[messageId][emoji];
                    }
                    if (Object.keys(newReactions[messageId]).length === 0) {
                        delete newReactions[messageId];
                    }
                }

                return newReactions;
            });

            return false;
        }
    }, [imsdk, isLogged, currentUserID, t, ensureSDKSessionMatchesCurrentUser]);

    // ============================================
    // Typing Status
    // ============================================

    const sendTypingStatus = useCallback((conversationID: string, isTyping: boolean = true) => {
        if (!imsdk || !isLogged || !conversationID) return;

        try {
            const operationID = Date.now().toString();
            imsdk.changeInputStates({
                conversationID,
                focus: isTyping
            }, operationID);
        } catch (err) {
            console.error('Failed to send typing status:', err);
        }
    }, [imsdk, isLogged]);

    // ============================================
    // Group Management
    // ============================================

    const openGroup = useCallback(async (groupID: string) => {
        if (!imsdk || !isLogged || !groupID) return;

        const operationID = Date.now().toString();

        // Call backend API to open the room (groupID = customerId)
        if (jfcToken) {
            try {
                await openRoom(jfcToken, groupID);
            } catch (err) {
                console.debug('openRoom API error (non-critical):', err);
            }
        }

        try {
            await imsdk.setGroupInfo({ groupID, introduction: 'open' }, operationID);
        } catch (err) {
            console.debug('setGroupInfo (open) error (non-critical):', err);
        }

        try {
            fetchConversations(imsdk);
        } catch (fetchErr) {
            console.debug('fetchConversations error:', fetchErr);
        }
    }, [imsdk, isLogged, fetchConversations, jfcToken]);

    const closeGroup = useCallback(async (groupID: string) => {
        if (!imsdk || !isLogged || !groupID) return;

        const operationID = Date.now().toString();

        // Call backend API to close the room (groupID = customerId)
        if (jfcToken) {
            try {
                await closeRoom(jfcToken, groupID);
            } catch (err) {
                console.debug('closeRoom API error (non-critical):', err);
            }
        }

        try {
            await imsdk.setGroupInfo({ groupID, introduction: 'close' }, operationID);
        } catch (err) {
            console.debug('setGroupInfo (close) error (non-critical):', err);
        }

        try {
            fetchConversations(imsdk);
        } catch (fetchErr) {
            console.debug('fetchConversations error:', fetchErr);
        }
    }, [imsdk, isLogged, fetchConversations, jfcToken]);

    /**
     * Read receipt send flow:
     * 1) Mark conversation as read in OpenIM (`markConversationMessageAsRead`).
     * 2) Send OpenIM group read receipts for live updates.
     * 3) When a sales rep reads customer messages, also persist a silent
     *    `read_status` control message so the customer can recover "Read"
     *    after account switches and new logins.
     * 4) If current user is a customer, keep the existing JFC backend read marker.
     *
     * This function is invoked by `tryMarkAsRead`, which runs only while chat is
     * actively viewed (visible tab, focused window, and opened room).
     */
    const markAsRead = useCallback(async (conversationID: string) => {
        if (!imsdk || !isLogged || !conversationID) return;
        if (markAsReadInFlight.current.has(conversationID)) return;
        markAsReadInFlight.current.add(conversationID);
        clearUnreadCountForConversation(conversationID);

        try {
            const operationID = Date.now().toString();

            // Try to mark as read - don't let failures block the rest of the function
            try {
                await imsdk.markConversationMessageAsRead(conversationID, operationID);
            } catch (markErr) {
                // Silently ignore mark as read errors - this is not critical
                console.debug('markConversationMessageAsRead error (non-critical):', markErr);
            }

            // Refresh conversations regardless of mark as read result
            try {
                fetchConversations(imsdk);
            } catch (fetchErr) {
                console.debug('fetchConversations error:', fetchErr);
            }

            if (!selectedRoom?.groupID) return;

            const readReceiptMessages = messages.filter(m => {
                if (!m.clientMsgID || m.sendID === currentUserID) return false;
                if (m.groupID && m.groupID !== selectedRoom.groupID) return false;
                if (isAlbumChildMessage(m)) return false;

                // Sales reps may need to repair already-read messages that never
                // got a durable read receipt before this fix.
                if (userType === UserType.SalesRep) {
                    return !sentPersistentReadReceiptMsgIds.current.has(m.clientMsgID);
                }

                if (sentReadReceiptMsgIds.current.has(m.clientMsgID)) return false;

                return !m.isRead;
            });

            const messageIds = readReceiptMessages
                .map(m => m.clientMsgID)
                .filter((id): id is string => !!id);

            if (messageIds.length === 0) return;

            let sentAnyReceipt = false;

            try {
                if (typeof imsdk.sendGroupMessageReadReceipt === 'function') {
                    const receiptResult = await imsdk.sendGroupMessageReadReceipt({
                        conversationID,
                        clientMsgIDList: messageIds,
                    }, operationID);

                    if (receiptResult?.errCode && receiptResult.errCode !== 0) {
                        console.debug('sendGroupMessageReadReceipt error (non-critical):', receiptResult.errMsg);
                    } else {
                        sentAnyReceipt = true;
                    }
                }
            } catch (receiptErr) {
                console.debug('sendGroupMessageReadReceipt error (non-critical):', receiptErr);
            }

            const shouldPersistSalesReadStatus = userType === UserType.SalesRep;
            const shouldSendCustomerReadStatus = userType === UserType.Customer;
            let sentPersistentReadStatus = false;

            if (shouldPersistSalesReadStatus || shouldSendCustomerReadStatus) {
                try {
                    const lastMessageID = messageIds[messageIds.length - 1];
                    const shouldSendReadStatusMessage =
                        shouldPersistSalesReadStatus ||
                        lastNotifiedMessageID.current[conversationID] !== lastMessageID;

                    if (shouldSendReadStatusMessage) {
                        const message = await createReadStatusMessage(
                            imsdk,
                            currentUserID,
                            messageIds,
                            operationID
                        );

                        if (message) {
                            const isOnlineOnlyReadStatus = !shouldPersistSalesReadStatus;
                            const sendOptions = {
                                recvID: '',
                                groupID: selectedRoom.groupID,
                                message,
                                isOnlineOnly: isOnlineOnlyReadStatus,
                                offlinePushInfo: buildReadStatusOfflinePushInfo(selectedRoom.groupID, conversationID),
                            };
                            await imsdk.sendMessage(sendOptions, operationID);
                            sentAnyReceipt = true;
                            sentPersistentReadStatus = shouldPersistSalesReadStatus;

                            if (userType === UserType.Customer) {
                                console.log(`Notifying backend for the last read message: ${lastMessageID}`);
                                markMessageAsRead(jfcToken, {
                                    clientMsgID: lastMessageID,
                                    customerID: selectedRoom.groupID,
                                    userEmail: currentEmail
                                }).then(() => {
                                    lastNotifiedMessageID.current[conversationID] = lastMessageID;
                                    try {
                                        localStorage.setItem('jfc_chat_last_notified_msg_id', JSON.stringify(lastNotifiedMessageID.current));
                                    } catch (e) {
                                        console.debug('Failed to save lastNotifiedMessageID to localStorage:', e);
                                    }
                                }).catch(err => {
                                    console.error('Failed to notify backend about read status:', err);
                                });
                            }
                        }
                    }
                } catch (customMsgErr) {
                    console.debug('custom read_status error (non-critical):', customMsgErr);
                }
            }

            if (sentAnyReceipt) {
                messageIds.forEach(id => sentReadReceiptMsgIds.current.add(id));
                try {
                    const idsArray = Array.from(sentReadReceiptMsgIds.current);
                    if (idsArray.length > 1000) {
                        sentReadReceiptMsgIds.current = new Set(idsArray.slice(-1000));
                    }
                    localStorage.setItem('jfc_chat_sent_read_receipts', JSON.stringify(Array.from(sentReadReceiptMsgIds.current)));
                } catch (e) {
                    console.debug('Failed to save read receipt IDs to localStorage:', e);
                }

                if (sentPersistentReadStatus) {
                    messageIds.forEach(id => sentPersistentReadReceiptMsgIds.current.add(id));
                    try {
                        const idsArray = Array.from(sentPersistentReadReceiptMsgIds.current);
                        if (idsArray.length > 1000) {
                            sentPersistentReadReceiptMsgIds.current = new Set(idsArray.slice(-1000));
                        }
                        localStorage.setItem(
                            'jfc_chat_sent_persistent_read_receipts',
                            JSON.stringify(Array.from(sentPersistentReadReceiptMsgIds.current))
                        );
                    } catch (e) {
                        console.debug('Failed to save persistent read receipt IDs to localStorage:', e);
                    }
                }

                const readTimestamp = Date.now();
                setMessages(prev => prev.map(m => {
                    if (messageIds.includes(m.clientMsgID)) {
                        return { ...m, isRead: true, readAt: readTimestamp };
                    }
                    return m;
                }));
            }
        } finally {
            markAsReadInFlight.current.delete(conversationID);
        }
    }, [imsdk, isLogged, fetchConversations, userType, currentUserID, currentEmail, jfcToken, messages, selectedRoom, createReadStatusMessage, clearUnreadCountForConversation]);

    // Check if the user is actively viewing the chat (tab visible + window focused + chat open)
    const isChatActivelyViewed = useCallback(() => {
        if (typeof document === 'undefined') return false;
        return (
            document.visibilityState === 'visible' &&
            document.hasFocus() &&
            (layoutState === LayoutState.Minimize || layoutState === LayoutState.Maximize) &&
            !!selectedRoom?.conversationID
        );
    }, [layoutState, selectedRoom]);

    // Attempt to mark unread messages as read if the chat is actively viewed
    const tryMarkAsRead = useCallback(() => {
        if (!isLogged || !isChatActivelyViewed() || !selectedRoom?.conversationID) return;

        const conversationID = selectedRoom.conversationID;
        const currentRoom = rooms.find(room =>
            room.conversationID === conversationID ||
            (!!selectedRoom.groupID && room.groupID === selectedRoom.groupID)
        );
        const currentMessageCount = messages.filter(m => m.groupID === selectedRoom.groupID).length;

        // Prevent duplicate sends when just switching layout modes (minimize <-> maximize)
        const lastSent = lastReadReceiptSent.current[conversationID];
        if (lastSent) {
            const timeSinceLastSent = Date.now() - lastSent.timestamp;
            if (timeSinceLastSent < 5000 && lastSent.messageCount === currentMessageCount) {
                return;
            }
        }

        const messagesFromOthers = messages.filter(m =>
            m.sendID !== currentUserID &&
            m.groupID === selectedRoom.groupID &&
            !isAlbumChildMessage(m)
        );

        const hasUnreadFromOthers = messagesFromOthers.some(m => !m.isRead);
        const hasMissingSalesReceipt = userType === UserType.SalesRep && messagesFromOthers.some(m =>
            !!m.clientMsgID && !sentPersistentReadReceiptMsgIds.current.has(m.clientMsgID)
        );
        const hasSdkUnreadCount =
            Number(selectedRoom.conversation?.unreadCount || 0) > 0 ||
            Number(currentRoom?.conversation?.unreadCount || 0) > 0;

        if (hasUnreadFromOthers || hasMissingSalesReceipt || hasSdkUnreadCount) {
            lastReadReceiptSent.current[conversationID] = {
                timestamp: Date.now(),
                messageCount: currentMessageCount
            };
            markAsRead(selectedRoom.conversationID);
        }
    }, [isLogged, isChatActivelyViewed, selectedRoom, rooms, messages, currentUserID, userType, markAsRead]);

    // Auto-mark as read when chat is open, visible, focused, and new messages arrive
    useEffect(() => {
        tryMarkAsRead();
    }, [tryMarkAsRead]);

    // Handle app background/foreground transitions (especially important on iOS)
    // - Tells OpenIM server to switch to FCM push delivery when backgrounded
    // - Refreshes messages and re-registers FCM token when returning to foreground
    // - Marks unread messages as read when user returns and chat is visible
    useEffect(() => {
        if (typeof document === 'undefined') return;

        const handleVisibilityChange = async () => {
            if (!imsdk || !isLogged) return;

            const isHidden = document.visibilityState === 'hidden';

            // Tell OpenIM server whether the app is in background.
            // When background=true, the server uses FCM push instead of WebSocket
            // (which iOS kills when the app is inactive).
            try {
                const opID = Date.now().toString();
                await imsdk.setAppBackgroundStatus(isHidden, opID);
            } catch (e) {
                console.debug('setAppBackgroundStatus failed (non-critical):', e);
            }

            // When coming back to foreground, refresh everything
            if (!isHidden) {
                // Re-register the active push token. In Flutter WebView this uses
                // the native bridge; in browser/PWA it keeps the web FCM path.
                try {
                    notificationService.refreshTokenRegistration().catch(() => {});
                } catch (e) {
                    // Non-critical
                }

                const room = selectedRoomRef.current;
                if (!room?.conversationID) return;

                // Small delay to let the SDK finish reconnecting/syncing
                await new Promise(resolve => setTimeout(resolve, 500));

                try {
                    // Reload latest messages from SDK's local database
                    const freshMessages = await loadMessages(room.conversationID);
                    if (freshMessages.length > 0) {
                        // Merge with existing messages: keep new ones that aren't already displayed
                        setMessages(prev => {
                            return freshMessages.reduce(
                                (next, message) => upsertMessageByClientID(next, message),
                                prev
                            );
                        });
                    }
                } catch (e) {
                    console.debug('Failed to refresh messages on resume:', e);
                }

                // Also refresh conversations
                try {
                    fetchConversations(imsdk);
                } catch (e) {
                    console.debug('Failed to refresh conversations on resume:', e);
                }

                // Mark any unread messages as read now that user is back
                tryMarkAsRead();
            }
        };

        // When user clicks back into the browser window (e.g. from another app on second monitor)
        const handleWindowFocus = () => {
            tryMarkAsRead();
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleWindowFocus);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleWindowFocus);
        };
    }, [imsdk, isLogged, loadMessages, fetchConversations, tryMarkAsRead]);

    // ============================================
    // Image & Video Viewer
    // ============================================

    const openImageViewer = useCallback((images: string[], currentIndex: number) => {
        setImageViewer({ images, currentIndex });
    }, []);

    const openVideoViewer = useCallback((urls: string[], currentIndex: number) => {
        setVideoViewer({ urls, currentIndex });
    }, []);

    const closeImageViewer = useCallback(() => {
        setImageViewer(null);
    }, []);

    const closeVideoViewer = useCallback(() => {
        setVideoViewer(null);
    }, []);

    const nextImage = useCallback(() => {
        setImageViewer(prev => {
            if (!prev) return null;
            const nextIndex = (prev.currentIndex + 1) % prev.images.length;
            return { ...prev, currentIndex: nextIndex };
        });
    }, []);

    const prevImage = useCallback(() => {
        setImageViewer(prev => {
            if (!prev) return null;
            const prevIndex = (prev.currentIndex - 1 + prev.images.length) % prev.images.length;
            return { ...prev, currentIndex: prevIndex };
        });
    }, []);

    const nextVideo = useCallback(() => {
        setVideoViewer(prev => {
            if (!prev) return null;
            const nextIndex = (prev.currentIndex + 1) % prev.urls.length;
            return { ...prev, currentIndex: nextIndex };
        });
    }, []);

    const prevVideo = useCallback(() => {
        setVideoViewer(prev => {
            if (!prev) return null;
            const prevIndex = (prev.currentIndex - 1 + prev.urls.length) % prev.urls.length;
            return { ...prev, currentIndex: prevIndex };
        });
    }, []);

    // ============================================
    // Context Value
    // ============================================

    const value: ChatContextType = {
        imsdk,
        isLogged,
        connectionStatus,
        currentUserID,
        currentEmail,
        userType,
        jfcToken,
        layoutState,
        positionState,
        setLayoutState,
        setPositionState,
        rooms,
        selectedRoom,
        conversations,
        selectRoom,
        getRoomUnreadCount,
        messages,
        loadMessages,
        sendTextMessage,
        sendImageMessage,
        sendVideoMessage,
        sendFileMessage,
        sendSoundMessage,
        sendLocationMessage,
        sendReplyMessage,
        sendAlbumMessage,
        revokeMessage,
        deleteMessageForMe,
        sendReaction,
        messageReactions,
        typingStatus,
        sendTypingStatus,
        openGroup,
        closeGroup,
        markAsRead,
        imageViewer,
        videoViewer,
        openImageViewer,
        openVideoViewer,
        closeImageViewer,
        closeVideoViewer,
        nextImage,
        prevImage,
        nextVideo,
        prevVideo,
        init,
        logout,
        updateGroupFilter,
        searchMessages,
        isActiveMember,
        checkMembership
    };

    return (
        <ChatContext.Provider value={value}>
            {children}
        </ChatContext.Provider>
    );
}
